import {
  React,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useRef,
} from 'react';
import {
  OverlayTrigger,
  Popover,
  Modal,
  Form,
  Button,
  Row,
  Col,
  Nav,
  SplitButton,
  Dropdown,
  DropdownButton,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Grid3x3, Grid3x2, Grid2x3, Grid2x2 } from './SvgIcons';
import { Icon } from '@iconify/react';
import { ChromePicker } from 'react-color';
import ColorList from './ColorList';
import CustomInput from './CustomInput';
import { useTranslation, Trans } from 'react-i18next';
import { hasAllowedLicence } from '../../../../data/allowedLicenses';
import LoadingLogo from '../../../general/LoadingLogo';
import TagNameField from './TagNameField';
import api from '../../../../api';
import Badge from 'react-bootstrap/Badge';
import { getError } from '../../../../utils';
import {
  MIXED,
  collectTargetEntries,
  computeCommonSettings,
  getByPath,
  isMixed,
  pickMajorityColor,
  resolveSettingValue,
  uniformColorInfo,
  updateNestedField,
  validateTagRename,
} from '../../../../utils/multiSelection';
import {
  ICON_FIELD_PATHS,
  defaultCollection,
  defaultStyle,
  defaultIsActiveStates,
  defaultSelectedIconColorIndexes,
} from './iconEntryDefaults';
import {
  calculatePngDpiQuality,
  fetchIconifySvg,
  adaptIconUpdates,
  iconifyIconUpdates,
  isAnimatedSVG,
  isPngIconEntry,
  normalizeIconObject,
  parseSvgDimensions,
  supportsSvgIconSettings,
  toggleFlipValue,
  uploadedIconUpdates,
} from '../../../../utils/iconEntries';
import { deepClone } from '../../../../utils/deepObjects';
import { rgbaToHex } from '../../../../utils/colorHelpers';

// The following objects are declared outside the component, as they are static (don't depend on any other variables) and don't change:
const TAG_NAME_KEYS = {
  empty: 'icon-editor-component-name-empty',
  'too-long': 'icon-editor-component-name-too-long',
  'already-in-use': 'icon-editor-component-name-already-in-use',
}; // the toast for each reason validateTagRename (utils/multiSelection.js) refuses a tag name

const ICON_COLOR_SETTINGS = ['fill', 'stroke']; // the colour settings of an icon entry, each with its colour list

const IconEntryPreview = ({
  userInfo,
  iconObj,
  userIconPreviewsById,
  refreshAllRelevantUserIconPreviewsOnce,
}) => {
  const { t } = useTranslation();

  const normalizedIcon = normalizeIconObject(iconObj); // adds missing default icon fields so older saved Iconify icons and new uploaded icons can be handled with one shape
  const source = normalizedIcon.source || 'iconify';

  if (source === 'user_asset') {
    const userIconPreview =
      userIconPreviewsById[String(normalizedIcon.assetId)];

    if (!userIconPreview?.previewUrl) {
      return (
        <div
          className="missing-user-icon-preview"
          title={
            userInfo
              ? t('icon-editor-component-uploaded-icon-preview-unavailable')
              : t(
                  'icon-editor-component-login-again-restore-uploaded-icon-previews'
                )
          }
        >
          ?
        </div>
      );
    }

    return (
      <img
        src={userIconPreview.previewUrl}
        alt={
          userIconPreview.displayName || userIconPreview.originalFilename || ''
        }
        height="75%"
        width="75%"
        className="icon-entry-preview-image"
        onError={() => {
          refreshAllRelevantUserIconPreviewsOnce(); // refreshes signed preview URLs for uploaded icons if one has expired or failed to load
        }}
      />
    );
  }

  return normalizedIcon.icon ? (
    <Icon icon={normalizedIcon.icon} height="75%" width="75%" />
  ) : null;
}; // renders the icon entry preview using Iconify icons or uploaded icon preview images

const IconEditor = forwardRef(
  (
    {
      userInfo,
      isActive,
      design,
      updateDesign,
      predefinedTagColors,
      userIconPreviews,
      userIconPreviewsById,
      userIconAssetsById,
      fetchUserIconPreviews,
      loadingUserIcons,
      setUserIconPreviews,
      setUserIconAssetsById,
      refreshAllRelevantUserIconPreviewsOnce,
      removeDeletedUserIconFromCurrentDesign,
      removeDeletedUserIconFromHistoryStacks,
      isUserIconUsedInIndexedDBCartDesigns,
    },
    ref
  ) => {
    // We use forwardRef to allow the parent component (CustomizerScreen.js) to access functions or values inside IconEditor.
    // This is useful when the parent needs to directly call methods (like triggering an update) on the child.
    const { t } = useTranslation();

    const keycapData = design.keycapData;
    const { selectedIconIndex, colors, tags, selectedColorEntry, selectedTag } =
      design.iconEditor;
    const {
      selectedEditor,
      selectedKeycaps = [],
      isoEnterSelected,
      showGrid,
      showBoundingBox,
      // showPrintableArea, // uncomment together with the disabled Printable Area checkbox below
    } = design.settings;
    const [showIconSearch, setShowIconSearch] = useState(true); // State to control whether the icon search section is shown or collapsed
    const [screenWidthXL, setScreenWidthXL] = useState(
      window.innerWidth <= 1199
    ); // State to track whether the screen size is <= 1199px
    const [showIconEntries, setShowIconEntries] = useState(true); // State to control whether the icon entries panel is shown or collapsed
    const [screenWidthMD, setScreenWidthMD] = useState(
      window.innerWidth <= 767
    ); // State to track whether the screen size is <= 767px
    const [showIconSettings, setShowIconSettings] = useState(true); // State to control whether the settings panel is shown or collapsed
    const [showPositionSettings, setShowPositionSettings] = useState(true); // State to control whether the settings panel is shown or collapsed
    const [showIconStyleSettings, setShowIconStyleSettings] = useState(true); // State to control whether the settings panel is shown or collapsed
    const [query, setQuery] = useState(''); // stores the icon search query entered by the user
    const [icons, setIcons] = useState([]); // stores the fetched icons that match the search query
    const [collections, setCollections] = useState({}); // stores the collections metadata for each icon collection
    const [showTagManager, setShowTagManager] = useState(false); // Modal visibility
    const [newTagName, setNewTagName] = useState(
      t('icon-editor-component-new-tag')
    );
    const [newTagColor, setNewTagColor] = useState(
      predefinedTagColors.find(
        (predefinedColor, index) => !tags.some((tag) => tag.id === index + 1)
      ) // Find the first color in predefinedTagColors that is not used in any tag (this is needed during initialization because tags may be imported from localStorage or an imported design)
    );
    const [selectedTagPosition, setSelectedTagPosition] = useState(null); // stores the selected tag (the position of the tag that the user clicked on)
    const [deleteIconColorMode, setDeleteIconColorMode] = useState(false); // state to toggle delete icon colors mode on and off
    const [hoveredIndex, setHoveredIndex] = useState(null); // state to save the index of the tag the user over over
    const [showTagDropdown, setShowTagDropdown] = useState(false);
    const tagDropdownRef = useRef(null); // ref to the tag dropdown menue
    const menuRef = useRef(null); // ref the the <Dropdown.Menu> element

    const [selectedCollectionPrefix, setSelectedCollectionPrefix] =
      useState('all'); // currently selected icon set prefix; 'all' means no specific set filter
    const [availableCollections, setAvailableCollections] = useState({}); // all allowed icon sets shown in the dropdown
    const [isLoadingIcons, setIsLoadingIcons] = useState(false); // whether icon results are currently being fetched
    const allowedPrefixes = Object.keys(availableCollections); // get all allowed icon set prefixes for server-side filtering

    const activeIconLibraryTab =
      design.iconEditor?.activeIconLibraryTab || 'built-in';

    const showUserIconLibraryLoading =
      loadingUserIcons && userIconPreviews.length === 0; // only replace the grid while there is nothing to show yet — a background refresh keeps the current previews mounted, so their thumbnails are not re-requested
    const [isUploadingUserIcon, setIsUploadingUserIcon] = useState(false);
    const [isDeletingUserIcons, setIsDeletingUserIcons] = useState(false);
    const [deletingUserIconId, setDeletingUserIconId] = useState(null);

    const defaultIconPosition = useMemo(
      () => ({
        isoSection: isoEnterSelected ? 'top' : 'none',
        gridStructure: '3x3',
        gridRow: 2,
        gridColumn: 2,
        cellAlignment: 'center',
        boundingBox: 'compact',
        rotation: 0,
        xOffset: 0,
        yOffset: 0,
      }),
      [isoEnterSelected]
    ); // Keep this object’s reference stable with useMemo. Without it, a new object is created every render (triggering needless work and deps warnings). It only recalculates when the listed deps change.
    const defaultIconObject = useMemo(
      () => ({
        source: 'iconify',
        icon: 'formkit:happy', // the string needed to access a specific icon in the <Icon /> component from iconify
        iconCollection: defaultCollection, // need to use the name 'iconCollection' instead of 'collection', because `collection` is a reserved schema pathname in mongoose and may break some functionality
        assetId: null,
        format: null,
        style: defaultStyle,
        position: defaultIconPosition,
        isActiveStates: defaultIsActiveStates,
        selectedIconColorIndexes: defaultSelectedIconColorIndexes,
        tagId: null,
        visible: true,
      }),
      [defaultIconPosition]
    ); // Keep this object’s reference stable with useMemo. Without it, a new object is created every render (triggering needless work and deps warnings). It only recalculates when the listed deps change.

    const {
      exactlyOneSelected,
      selectedIconObject,
      selectedTagId,
      singleSelectedKeycap,
    } = useMemo(() => {
      const exactlyOneSelected = selectedKeycaps.length === 1; // check if the user has exactly one keycap selected

      let selectedIconObject = defaultIconObject;
      let selectedTagId = null;
      let singleSelectedKeycap = null;

      if (exactlyOneSelected) {
        const { rowIndex, keycapIndex } = selectedKeycaps[0];
        const kc = keycapData?.[rowIndex]?.[keycapIndex];

        if (kc && !kc.isSpacer) {
          selectedIconObject =
            kc.icons?.[selectedIconIndex] ?? defaultIconObject;
          selectedTagId = kc.icons?.[selectedTagPosition]?.tagId ?? null;
          singleSelectedKeycap = { rowIndex, keycapIndex };
        }
      }

      return {
        exactlyOneSelected,
        selectedIconObject,
        selectedTagId,
        singleSelectedKeycap,
      };
    }, [
      selectedKeycaps,
      keycapData,
      selectedIconIndex,
      selectedTagPosition,
      defaultIconObject,
    ]); // useMemo ensures that the exactlyOneSelected, selectedIconObject and selectedTagId variables will be recomputed if any of the dependencies change

    const targets = collectTargetEntries({
      keycapData,
      entriesKey: 'icons',
      selectedKeycaps,
      selectedIndex: selectedIconIndex,
      selectedTag,
    }); // the icon entries the settings apply to (none for a single selected icon entry)
    const isControlDisabled =
      targets.length === 0 && !(exactlyOneSelected && selectedIconIndex); // enabled controls when at least one keycap is selected and at least one selected keycap has an icon entry, or if the user has exactly one keycap and icon entry selecred, or if the user has selected a tag which is referenced by at least one text entry

    const normalizedSelectedIcon = normalizeIconObject(selectedIconObject); // adds missing default icon fields so older saved Iconify icons and new uploaded icons can be handled with one shape
    const selectedIconIsPng = isPngIconEntry(normalizedSelectedIcon); // PNG uploaded icons need separate compatibility handling because some SVG-only controls do not apply to them

    const iconSettingTargets = useMemo(
      () =>
        collectTargetEntries({
          keycapData,
          entriesKey: 'icons',
          selectedKeycaps,
          selectedIndex: selectedIconIndex,
          selectedTag,
          includeSingle: true,
        }),
      [keycapData, selectedKeycaps, selectedIconIndex, selectedTag]
    ); // the icon entries the settings panel edits, or the single selected one

    const hasSvgCompatibleIconTarget = iconSettingTargets.some((iconObj) =>
      supportsSvgIconSettings(iconObj)
    ); // true if at least one targeted icon can receive fill/stroke/boundingBox settings

    const pngIncompatibleControlsDisabled =
      (iconSettingTargets.length > 0 && !hasSvgCompatibleIconTarget) ||
      (exactlyOneSelected && selectedIconIndex != null && selectedIconIsPng); // disable SVG-only controls when the current target selection contains only PNG uploaded icons

    const excludedCollections = ['svg-spinners', 'line-md']; // List of collection prefixes to exclude, such as sets with animations or other unwanted icons

    const visibleIcons = useMemo(() => {
      return icons.filter((icon) => {
        const [collectionName] = icon.split(':'); // get the icon set prefix from the icon name (e.g., "mdi:home" -> "mdi")
        return !!availableCollections[collectionName]; // only keep icons from allowed icon sets
      }); // filter icons based on allowed licenses and excluded collections
    }, [icons, availableCollections]); // memoize visibleIcons, to avoid re-filtering on every render

    const [tempWidth, setTempWidth] = useState(''); // this temp value allows the user to type in an invalid number, before it is validated
    const [tempHeight, setTempHeight] = useState(''); // this temp value allows the user to type in an invalid number, before it is validated

    const didInitTempDimensionsRef = useRef(false);

    useEffect(() => {
      let isMounted = true; // track whether the component is still mounted (to prevent state updates after the component has unmounted)

      const loadCollections = async () => {
        try {
          const response = await fetch(
            'https://api.iconify.design/collections'
          ); // fetch all icon sets from Iconify
          const data = await response.json(); // parse the response as JSON

          const filteredCollections = Object.fromEntries(
            Object.entries(data || {}).filter(
              ([prefix, collection]) =>
                collection && // only keep valid collections
                hasAllowedLicence(collection) && // only keep collections with an allowed license
                !excludedCollections.includes(prefix) // exclude blocked icon sets
            )
          );

          if (!isMounted) return; // stop if the component was unmounted

          setAvailableCollections(filteredCollections); // save allowed collections for the dropdown
          setCollections(filteredCollections); // keep collection metadata available for icon info / selection
        } catch (err) {
          console.error('Error fetching icon collections:', err);
          if (!isMounted) return; // stop if the component was unmounted
          setAvailableCollections({}); // fall back to an empty collection list
        }
      };

      loadCollections(); // load collections once when the component mounts

      return () => {
        isMounted = false; // mark the component as unmounted during cleanup
      };
    }, []); // fetches all Iconify collections once, filters them by allowed licenses/exclusions, and stores the allowed sets for the dropdown

    useEffect(() => {
      if (!Object.keys(availableCollections).length) return; // wait until the allowed icon sets have been loaded

      searchIcons({
        nextQuery: query, // use the current search text
        nextCollectionPrefix: selectedCollectionPrefix, // use the currently selected icon set
      });
    }, [selectedCollectionPrefix, availableCollections]); // reloads icons when the selected icon set changes

    useEffect(() => {
      if (selectedIconIndex == null) return;

      const unselectOnEscape = (e) => {
        if (e.key === 'Escape') {
          updateDesign({ 'iconEditor.selectedIconIndex': null });
        }
      };

      window.addEventListener('keydown', unselectOnEscape);
      return () => window.removeEventListener('keydown', unselectOnEscape);
    }, [selectedIconIndex, updateDesign]); // Escape unselects the icon entry, the keyboard equivalent of clicking next to the entries

    useEffect(() => {
      if (didInitTempDimensionsRef.current) return; // prevent double init (e.g., React 18 StrictMode)
      didInitTempDimensionsRef.current = true;

      // 2. Set UI states
      // 2.1 If the user has selected a specific icon entry, set dimension states based on the values of the selected icon entry:
      const exactlyOne = selectedKeycaps.length === 1;
      let initializedFromSelection = false;

      if (exactlyOne && selectedIconIndex != null) {
        // if the user has selected exactly one keycap and also selected a specific icon entry:
        const { rowIndex, keycapIndex } = selectedKeycaps[0];
        const icon =
          keycapData?.[rowIndex]?.[keycapIndex]?.icons?.[
            String(selectedIconIndex)
          ] ?? null; // get the data of the selected icon entry

        if (icon) {
          // set temp width and height states based on the selected icon entry:
          setTempWidth(icon.style.width);
          setTempHeight(icon.style.height);

          initializedFromSelection = true;
        }
      }

      // 2.2 If the user has no tag, no keycaps and no icon entry selected, set the UI width and height states to the values of the defaults:
      if (!initializedFromSelection) {
        const hasSelection = selectedKeycaps.length > 0; // check if the user has at least one keycap selected
        const hasAnyIconsInSelection = selectedKeycaps.some(
          ({ rowIndex, keycapIndex }) => {
            const kc = keycapData?.[rowIndex]?.[keycapIndex];
            if (!kc || kc.isSpacer) return false;
            return Object.keys(kc.icons || {}).length > 0;
          }
        ); // check if any of the selected keycaps hold at least one icon entry (false if none of the selected keycaps have any icon entries)

        const hasAnyIconsForSelectedTag =
          collectTargetEntries({ keycapData, entriesKey: 'icons', selectedTag })
            .length > 0; // whether at least one icon entry references the selected tag (none without a tag)

        const shouldInitDefaults =
          (!hasSelection || !hasAnyIconsInSelection) &&
          !hasAnyIconsForSelectedTag; // init defaults only if: no valid keycap+icon selection AND no tag-driven targets exist

        if (shouldInitDefaults) {
          // if the user has no keycaps and no icon entries selected, set the temp width and height states to the defaults:
          setTempWidth(defaultStyle.width);
          setTempHeight(defaultStyle.height);
        }
      }

      // 2.3 The case, when the user has no specific icon entry selected, but has selected at least one keycap with at least one icon entry inside it, or has selected a tag that is referenced by at least one icon entry, is handled in a separate useEffect that depends on commonIconSettings.
    }, []); // useEffect hook to set/initialize tempWidth and tempHeight states

    useEffect(() => {
      // get references to the 3 relevant DOM elements:
      const iconContainer = document.querySelector('.icon-entries-container');
      const iconSearchContainer = document.querySelector(
        '.icon-search-container'
      );
      const targetContainer = document.querySelector(
        '.icon-entry-settings-container'
      );

      if (!iconContainer || !iconSearchContainer || !targetContainer) return;

      const updateHeight = () => {
        const targetHeight = targetContainer.offsetHeight; // get the current height of the settings panel
        const viewportWidth = window.innerWidth; // get current screen width

        // sync .icon-entries-container height — only if screen is wider than 767px:
        if (viewportWidth > 767) {
          iconContainer.style.height = `${targetHeight}px`;
        } else {
          iconContainer.style.height = ''; // reset height (allow natural flow on small screens)
        }

        // sync .icon-search-container height — only if screen is wider than 1199px:
        if (viewportWidth > 1199) {
          iconSearchContainer.style.height = `${targetHeight}px`;
        } else {
          iconSearchContainer.style.height = ''; // reset height
        }
      }; // function to sync heights depending on viewport width

      updateHeight(); // initial call to set heights on mount or dependency change
      window.addEventListener('resize', updateHeight); // recalculate on window resize

      return () => window.removeEventListener('resize', updateHeight); // cleanup: remove the resize listener when component unmounts or dependencies change
    }, [
      isActive,
      isoEnterSelected,
      showPositionSettings,
      showIconStyleSettings,
    ]); // this useEffect ensures that the heights of `.icon-entries-container` and `.icon-search-container` stay visually in sync with `.icon-entry-settings-container` — but only on larger screen sizes.

    useEffect(() => {
      const mediaQueryXL = window.matchMedia('(max-width: 1199px)'); // Media query for XL screens (≤1199px)
      const mediaQueryMD = window.matchMedia('(max-width: 767px)'); // Media query for MD screens (≤767px)

      const handleResize = () => {
        // Update XL screen state:
        const isXL = mediaQueryXL.matches;
        setScreenWidthXL(isXL);

        // Update MD screen state:
        const isMD = mediaQueryMD.matches;
        setScreenWidthMD(isMD);
      }; // Handler function that runs when the screen size crosses the 1199px and 767px thresholds

      handleResize(); // Set initial state on mount

      // Attach listeners to both media queries:
      mediaQueryXL.addEventListener('change', handleResize);
      mediaQueryMD.addEventListener('change', handleResize);

      return () => {
        // Cleanup event listeners on unmount:
        mediaQueryXL.removeEventListener('change', handleResize);
        mediaQueryMD.removeEventListener('change', handleResize);
      };
    }, []); // this useEffect updates the screenWidthXL and screenWidthMD states based on the screen size

    useEffect(() => {
      if (!showTagDropdown) return;

      const onDocClick = (e) => {
        if (!tagDropdownRef.current) return;
        if (!tagDropdownRef.current.contains(e.target))
          setShowTagDropdown(false);
      };

      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [showTagDropdown]); // if the tag dropdown is opened and the user clicks outside, the dropdown closes

    useEffect(() => {
      if (!showTagDropdown) return;

      const el = menuRef.current; // get the DOM element of the dropdown menu
      if (!el) return;

      const update = () => {
        const hasScrollbar = el.scrollHeight > el.clientHeight; // true if content height is bigger than container height
        el.setAttribute('data-scrollable', hasScrollbar ? 'true' : 'false'); // mark menu as scrollable or not (so that I can adjust the css in index.css based on if the menu shows a scroll bar)
      }; // function to check if a vertical scrollbar (inside the Dropdown.Menu) exists

      update(); // run initial measurement

      // keep measurements updated whenever the element's size/content changes:
      const ro = new ResizeObserver(update);
      ro.observe(el);

      window.addEventListener('resize', update); // also recheck measurements when the window resizes

      return () => {
        // cleanup on effect unmount or dependency change:
        ro.disconnect(); // stop observing size/content changes
        window.removeEventListener('resize', update); // remove resize listener
      };
    }, [showTagDropdown]); // runs when the dropdown menu is shown/hidden to measure whether it has a vertical scrollbar and update a custom 'data-scrollable' attribute variable accordingly (this variable is than used inside index.css to change the position of the menu, depending on if a scroll bar is added)

    useEffect(() => {
      if (userIconPreviews.length === 0) {
        setIsDeletingUserIcons(false); // leave delete mode automatically when there are no uploaded icons left to delete
      }
    }, [userIconPreviews.length]);

    const fetchCollectionIcons = async (prefix) => {
      const response = await fetch(
        `https://api.iconify.design/collection?prefix=${encodeURIComponent(
          prefix
        )}&info=1`
      ); // fetch all icons and metadata for the selected icon set

      if (!response.ok) {
        throw new Error(`Failed to fetch collection "${prefix}"`); // stop if the request failed
      }

      const data = await response.json(); // parse the response as JSON

      // The /collection response from Iconify does not provide a single flat icons array.
      // It splits visible icons into two buckets:
      // •	categories: icons grouped under category names
      // •	uncategorized: icons that are not in any category
      // To get all visible icons from the set, I need to combine both:
      const categoryIcons = Object.values(data.categories || {}).flat(); // get all icon names that belong to categories
      const uncategorizedIcons = data.uncategorized || []; // get all icon names that are not inside a category

      const uniqueIconNames = [
        ...new Set([...categoryIcons, ...uncategorizedIcons]),
      ]; // merge both icon lists and remove duplicates

      return {
        icons: uniqueIconNames.map((name) => `${prefix}:${name}`), // add the collection prefix to each icon name, because this is the format I use everywhere else and Iconify itself uses
        info: data.info || availableCollections[prefix], // return the collection metadata, with a fallback to local data
      };
    }; // fetches all icons of one icon set, removes duplicates, and returns them together with the set metadata

    const searchWithinCollection = async (prefix, searchQuery) => {
      const response = await fetch(
        `https://api.iconify.design/search?query=${encodeURIComponent(
          searchQuery
        )}&prefix=${encodeURIComponent(prefix)}&limit=999` // if no limit is set for the /search api request, iconify uses a default limit of 64, the max limit iconify allows is 999.
      ); // search only within the selected icon set

      if (!response.ok) {
        throw new Error(`Failed to search icons for "${prefix}"`); // stop if the request failed
      }

      const data = await response.json(); // parse the response as JSON

      return {
        icons: data.icons || [], // return the matching icons from the selected set
        collections: {
          ...(data.collections || {}), // include collection metadata returned by the search API
          ...(availableCollections[prefix]
            ? { [prefix]: availableCollections[prefix] }
            : {}), // make sure the selected collection metadata is always available
        },
      };
    }; // searches for icons inside one selected icon set and returns the matches together with the needed collection metadata

    const searchIcons = async ({
      nextQuery = query,
      nextCollectionPrefix = selectedCollectionPrefix,
    } = {}) => {
      const trimmedQuery = nextQuery.trim(); // remove leading and trailing spaces from the search text

      setIsLoadingIcons(true); // show the loading indicator while icons are being fetched

      try {
        // Case 1: a specific icon set is selected:
        if (nextCollectionPrefix !== 'all') {
          if (!trimmedQuery) {
            // if the user has NOT entered a search text:
            const data = await fetchCollectionIcons(nextCollectionPrefix); // load all icons from the selected icon set

            setIcons(data.icons || []); // show all icons from the selected icon set
            setCollections((prev) => ({
              ...prev,
              ...(data.info ? { [nextCollectionPrefix]: data.info } : {}),
            })); // keep the selected collection metadata available
          } else {
            // if the user also entered a search text, to filter icons within the selected icon set:
            const data = await searchWithinCollection(
              nextCollectionPrefix,
              trimmedQuery
            ); // search, based user-entered query, only inside the selected icon set

            setIcons(data.icons || []); // show only the matching icons from the selected icon set
            setCollections((prev) => ({
              ...prev,
              ...(data.collections || {}),
            })); // keep the returned collection metadata available
          }

          return; // stop here because the selected icon set case is fully handled
        }

        // Case 2: "all icon sets" is selected
        if (!trimmedQuery) {
          // if the user has NOT entered a search text:
          setIcons([]); // show no icons when no search text is entered
          setCollections(availableCollections); // keep allowed collection metadata available
          return; // stop because there is nothing to search for
        }

        const response = await fetch(
          `https://api.iconify.design/search?query=${encodeURIComponent(
            trimmedQuery
          )}&prefixes=${encodeURIComponent(
            allowedPrefixes.join(',') // restrict to allowed icon sets
          )}&limit=999` // if no limit is set for the /search api request, iconify uses a default limit of 64, the max limit iconify allows is 999.
        ); // search across all icon sets

        if (!response.ok) {
          throw new Error('Failed to search icons'); // stop if the request failed
        }

        const data = await response.json(); // parse the response as JSON

        setIcons(data.icons || []);
        setCollections((prev) => ({
          ...prev,
          ...availableCollections,
          ...(data.collections || {}),
        })); // keep all allowed collection metadata plus any metadata returned by the search
      } catch (err) {
        console.error('Error searching icons:', err);
        setIcons([]);
      } finally {
        setIsLoadingIcons(false); // hide the loading indicator after the request finishes
      }
    }; // loads icons based on the selected icon set and search text, then stores the matching icons and collection metadata

    const createTag = (name, color) => {
      const trimmedName = name.trim(); // remove leading/trailing spaces

      if (!trimmedName) {
        toast(t('icon-editor-component-name-empty'));
        return;
      } // prevent adding a new tag if the name is empty after trimming

      updateDesign({
        'iconEditor.tags': (prevTags) => {
          if (prevTags.length >= 20) {
            toast(t('icon-editor-component-maximum-of-20-tags'));
            return prevTags;
          } // Prevent adding a new tag if the maximum of 20 tags has been reached

          if (trimmedName.length > 100) {
            toast(t('icon-editor-component-name-too-long'));
            return prevTags;
          } // prevent adding a new tag if the name exceeds 100 characters

          if (!color || !predefinedTagColors.includes(color)) {
            toast(t('icon-editor-component-invalid-or-missing-color'));
            return prevTags;
          } // ensure the color is valid and present in predefinedTagColors. Reachable, although the palette disables the colours in use: undo and redo (Ctrl+Z, Ctrl+Y, which fire under the open tag manager too) change the tags beneath the chosen colour, so no colour (after the twentieth tag was undone) or a taken one can arrive here (checked Oct 2026)

          const colorIndex = predefinedTagColors.indexOf(color) + 1; // Determine the color index based on predefinedTagColors (Starting from 1, because 0 is treated as falsy value and using it as an ID can lead to unexpected behavior in conditional checks)

          if (prevTags.some((tag) => tag.id === colorIndex)) {
            toast(t('icon-editor-component-color-already-assigned'));
            return prevTags;
          } // Prevent adding a new tag with a duplicate color (see the guard above for how one arrives)

          if (
            prevTags.some(
              (tag) => tag.name.toLowerCase() === trimmedName.toLowerCase()
            )
          ) {
            toast(t('icon-editor-component-name-already-in-use'));
            return prevTags;
          } // Prevent adding a new tag with a duplicate name

          const newTag = {
            id: colorIndex, // Unique ID for the tag (1-20)
            name: trimmedName,
          };

          const updatedTags = [...prevTags, newTag]; // adding the new tag

          const firstAvailableColor = predefinedTagColors.find(
            (predefinedColor, index) =>
              !updatedTags.some((tag) => tag.id === index + 1)
          ); // Find the first color in predefinedTagColors that is not used in any tag

          setNewTagColor(firstAvailableColor || null); // set the new tag color to the first available color after creating the tag

          return updatedTags;
        },
      });
    }; // function to create new tags

    const deleteTag = (index) => {
      if (index < 0 || index >= tags.length) return; // invalid index

      const tag = tags[index];
      const tagId = tag?.id ?? null; // id of the tag we’re deleting

      let freedColor = null; // will hold the color freed by the deleted tag
      if (tagId != null) {
        const colorIdx = tagId - 1;
        if (colorIdx >= 0 && colorIdx < predefinedTagColors.length) {
          freedColor = predefinedTagColors[colorIdx]; // remember the freed color so we can set it after state update if needed
        }
      }

      updateDesign((prev) => {
        const tags = prev.iconEditor?.tags || [];
        const nextTags = tags.filter((_, i) => i !== index); // remove tag from list

        const nextKeycapData = prev.keycapData.map((row) =>
          row.map((kc) => {
            // go through all keycaps:
            if (!kc || kc.isSpacer || !kc.icons) return kc; // skip invalid, spacer and non-icon keycaps

            const icons = kc.icons;
            let changed = false;

            const updated = {};
            for (const [k, v] of Object.entries(icons)) {
              // go through all icon entries of the current keycap:
              if (v && v.tagId === tagId) {
                changed = true;
                updated[k] = { ...v, tagId: null }; // remove tag reference from the current icon entry, if it matches the deleted tag
              } else {
                updated[k] = v;
              }
            }

            return changed ? { ...kc, icons: updated } : kc;
          })
        );

        return {
          ...prev,
          iconEditor: {
            ...prev.iconEditor,
            tags: nextTags, // update tags list
          },
          keycapData: nextKeycapData, // update tag refs in texts
        };
      });

      if (newTagColor == null && freedColor) {
        setNewTagColor(freedColor); // if no color is selected for the "new tag" picker, adopt the freed color
      }
    }; // deletes a tag (by index) and removes its id from all icon entries

    const handleSelectCell = (gridRow, gridColumn) => {
      handleKeycapChange(
        'icons',
        {
          'position.gridRow': gridRow,
          'position.gridColumn': gridColumn,
        },
        selectedIconIndex
      );
    }; // updates selected row and column to the selected icon object

    const handleSelectCellAlignment = (alignment) => {
      handleKeycapChange(
        'icons',
        { 'position.cellAlignment': alignment },
        selectedIconIndex
      );
    }; // updates selected cellAlignment to the selected icon object

    const handleKeycapChange = async (
      property, // The property of the keycap to be updated (e.g., 'icons')
      updates, // Object containing sub-property paths and their new values
      iconPosition = null, // Optional parameter specifying the position of an icon object within the `icons` object
      addNew = false, // Flag indicating if a new icon object should be added
      deleteIconKey = null // Optional parameter for deleting a specific icon entry
    ) => {
      updateDesign((prevDesign) => {
        const newDesign = { ...prevDesign }; // Clone the previous design state
        const selectedKeycaps = prevDesign.settings?.selectedKeycaps || [];
        const exactlyOneSelected = selectedKeycaps.length === 1; // check if the user has exactly one keycap selected

        const selectedTag = prevDesign.iconEditor?.selectedTag ?? null; // get the selected tag
        const tagMode = selectedTag != null;

        const selectedSet = new Set(
          selectedKeycaps.map(
            ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
          )
        ); // create a fast lookup for all selected keycaps ("row:keycap")

        if (tagMode && (addNew || deleteIconKey != null)) {
          return prevDesign; // adding/deleting a specific icon entry does not make sense in tag mode
        }

        const hasIsoSectionUpdate =
          property === 'icons' &&
          Object.prototype.hasOwnProperty.call(
            updates || {},
            'position.isoSection'
          ); // detect if this update includes updating the 'position.isoSection' field of the icon entries

        const updateIconsOfKeycap = (
          keycap,
          perKeycapUpdates,
          shouldUpdateEntry
        ) => {
          if (!perKeycapUpdates || Object.keys(perKeycapUpdates).length === 0)
            return keycap; // nothing to apply

          const currentIcons = keycap.icons || {};
          const nextIcons = Object.entries(currentIcons).reduce(
            (acc, [pos, iconObj]) => {
              if (iconObj && shouldUpdateEntry(pos, iconObj)) {
                // if the current icon entry should be updated:
                const safeUpdates = adaptIconUpdates(perKeycapUpdates, iconObj); // the updates this entry takes (a PNG's SVG-only settings dropped, the compact box back when it stops being one)

                acc[pos] =
                  Object.keys(safeUpdates).length > 0
                    ? updateNestedField(iconObj, safeUpdates) // update the current icon entry
                    : iconObj; // only update the icon if there are still valid updates left
              } else {
                acc[pos] = iconObj; // otherwise return the icon entry as it is
              }
              return acc;
            },
            {}
          );
          return { ...keycap, icons: nextIcons };
        }; // helper: update all or filtered icon entries of a keycap

        const updateSingleIconOfKeycap = (keycap, pos, perKeycapUpdates) => {
          if (!perKeycapUpdates || Object.keys(perKeycapUpdates).length === 0)
            return keycap; // nothing to apply

          const currentIcons = keycap.icons || {};
          const currentIconObj = currentIcons[pos] || {};
          const safeUpdates = adaptIconUpdates(
            perKeycapUpdates,
            currentIconObj
          ); // the updates this entry takes (a PNG's SVG-only settings dropped, the compact box back when it stops being one)

          if (Object.keys(safeUpdates).length === 0) {
            return keycap; // nothing valid to apply to this icon
          }

          const updatedIconObj = updateNestedField(currentIconObj, safeUpdates);

          return {
            ...keycap,
            icons: { ...currentIcons, [pos]: updatedIconObj }, // set the updated icon entry back into the icons object
          };
        }; // Helper: apply updates to a specific icon position of a single keycap

        const singleSel = exactlyOneSelected ? selectedKeycaps[0] : null; // only allow adding/deleting icon entries, if exactly one keycap selected

        newDesign.keycapData = prevDesign.keycapData.map((row, rIndex) =>
          row.map((keycap, kIndex) => {
            // go through all keycaps inside keycapData:
            const isSelected = selectedSet.has(`${rIndex}:${kIndex}`); // check if the current keycap is selected
            const isActual = keycap && !keycap.isSpacer;
            if (!isActual) return keycap; // skip empties / spacers

            // 1. ADD NEW ICON ENTRY:
            if (property === 'icons' && addNew && !tagMode) {
              if (
                !exactlyOneSelected ||
                rIndex !== singleSel.rowIndex ||
                kIndex !== singleSel.keycapIndex
              ) {
                // adding icon entries is only allowed, if the user has exactly one keycap selected
                // skip the current keycap, if it is not the selected keycap
                return keycap;
              }

              const currentIcons = keycap.icons || {};
              const count = Object.keys(currentIcons).length;
              if (count >= 10) {
                toast(t('icon-editor-component-maximum-of-10-icons'));
                return keycap; // make sure the user can't add more than 10 icon entries to the keycap
              }

              const nextKey = String(count + 1);
              const nextKeycap = {
                ...keycap,
                icons: {
                  ...currentIcons,
                  [nextKey]: deepClone(defaultIconObject), // add the default icon entry
                },
              };

              setDeleteIconColorMode(false); // exit delete icon color mode

              // update the UI width and height values:
              setTempWidth(defaultIconObject.style.width);
              setTempHeight(defaultIconObject.style.height);

              newDesign.iconEditor = {
                ...prevDesign.iconEditor,
                selectedIconIndex: nextKey, // update selectedIconIndex in newDesign
              };
              return nextKeycap;
            }

            // 2. DELETE AN ICON ENTRY:
            if (property === 'icons' && deleteIconKey != null && !tagMode) {
              if (
                !exactlyOneSelected ||
                rIndex !== singleSel.rowIndex ||
                kIndex !== singleSel.keycapIndex
              ) {
                // adding icon entries is only allowed, if the user has exactly one keycap selected
                // skip the current keycap, if it is not the selected keycap
                return keycap;
              }

              // remove the icon entry and reindex the remaining:
              const currentIcons = keycap.icons || {};
              const updatedIcons = Object.entries(currentIcons)
                .filter(([key]) => key !== String(deleteIconKey)) // filter out all icon entries that don't match the deleteIconKey
                .map(([_, val]) => val) // get an array of remaining icon objects
                .reduce((acc, ico, idx) => {
                  acc[String(idx + 1)] = ico; // re-index the remaining icon starting from 1
                  return acc;
                }, {});

              // maintain selectedIconIndex in newDesign:
              const prevSelIdx = prevDesign.iconEditor?.selectedIconIndex; // get the previous selectedIconIndex
              if (prevSelIdx) {
                const selectedIndex = parseInt(prevSelIdx, 10);
                const deleteIndex = parseInt(deleteIconKey, 10);

                if (deleteIndex === selectedIndex) {
                  newDesign.iconEditor = {
                    ...prevDesign.iconEditor,
                    selectedIconIndex: null, // if the deleted icon is the current selectedIconIndex, the selectedIconIndex is set to null
                  };
                } else if (deleteIndex < selectedIndex) {
                  // if the deleted icon is before the current selectedIconIndex, decrement the selectedIconIndex:
                  newDesign.iconEditor = {
                    ...prevDesign.iconEditor,
                    selectedIconIndex: String(selectedIndex - 1), // keep it a string value, as all conditional checks of the selectedIconIndex are done to a index of an object (which is also a string value)
                  };
                }
              }

              return { ...keycap, icons: updatedIcons };
            }

            // 3. APPLY UPDATES:
            if (property === 'icons') {
              let perKeycapUpdates = updates;

              if (hasIsoSectionUpdate && !keycap.isIsoEnter) {
                // if the keycap update(s) includes updating the 'position.isoSection' setting and the current keycap is NOT an ISO Enter keycap:
                const { ['position.isoSection']: _omit, ...rest } =
                  updates || {}; // remove the 'position.isoSection' update for the current keycap (but keep other updates/fields)
                perKeycapUpdates = rest;
              }

              const hasAnyUpdate =
                perKeycapUpdates && Object.keys(perKeycapUpdates).length > 0;
              if (!hasAnyUpdate) return keycap; // if after removing the 'position.isoSection' update nothing remains, skip the current keycap

              // 3.1 Tag selected (update every icon entry that references the selected tag):
              if (tagMode) {
                const pred = (_pos, icon) => {
                  return !!icon && icon.tagId === selectedTag;
                }; // function that checks if an icon entry should be updated (when its tagId matches the selected tag)
                return updateIconsOfKeycap(keycap, perKeycapUpdates, pred); // apply the updates to all icons that reference the selected tag
              }

              // 3.2 Multiple keycaps selected (no need to check for a iconPosition here, because when the user has selected multiple keycaps, no icon entries will be displayed and can therefor also not be selected):
              if (selectedKeycaps.length > 1) {
                if (!isSelected) return keycap;
                const pred = () => true; // all entries of this keycap
                return updateIconsOfKeycap(keycap, perKeycapUpdates, pred); // apply to all icon entries of all selected keycaps
              }

              // 3.3 Single keycap selected:
              if (exactlyOneSelected && isSelected) {
                // 3.3.1 No specific icon entry selected
                if (iconPosition == null) {
                  const pred = () => true;
                  return updateIconsOfKeycap(keycap, perKeycapUpdates, pred); // update all icon entries of the single keycap
                }

                // 3.3.2 Specific icon entry selected
                return updateSingleIconOfKeycap(
                  keycap,
                  iconPosition,
                  perKeycapUpdates
                ); // update only that one
              }

              return keycap; // no keycaps selected -> no icon updates
            }

            return keycap;
          })
        );

        return newDesign;
      });
    }; // this function allows to dynamically add or delete an icon entry within selected keycaps, or update a selected icon entry (or all icon entries of all selected keycaps)

    const handleSelectIconEntry = (position) => {
      updateDesign({
        'iconEditor.selectedIconIndex': position,
      });

      const clickedIconObject =
        keycapData?.[singleSelectedKeycap?.rowIndex]?.[
          singleSelectedKeycap?.keycapIndex
        ]?.icons?.[position] ?? null; // accessing the icon object the user clicked on

      setDeleteIconColorMode(false); // Disable delete icon color mode

      // set temp width and height states:
      setTempWidth(clickedIconObject.style.width);
      setTempHeight(clickedIconObject.style.height);
    };

    useImperativeHandle(ref, () => ({
      handleKeycapChange,
      handleSelectIconEntry,
    })); // Expose the handleKeycapChange and handleSelectIconEntry functions to the parent (CustomizerScreen.js)

    const handleAddIconColor = (currentColor, setting, colorPath) => {
      updateDesign((prev) => {
        // 1) append the color to the palette
        const prevColors = prev.iconEditor?.colors ?? [];
        const newColors = [...prevColors, currentColor]; // Add new color
        const newIndex = newColors.length - 1; // Get index of the newly added color
        const hasSelection = (prev.settings?.selectedKeycaps || []).length > 0;

        if (!isControlDisabled) {
          // only try updating icon entries when there are any target icon entries:
          handleKeycapChange(
            'icons',
            {
              [`selectedIconColorIndexes.${setting}`]: newIndex, // automatically reference the new color for the specified setting
              [colorPath]: currentColor, // apply the color to the setting
            },
            hasSelection ? (prev.iconEditor?.selectedIconIndex ?? null) : null // in tagMode we pass null (in tag mode there is no selected icon entry)
          );
        }

        // 3) commit palette change
        return {
          ...prev,
          iconEditor: {
            ...prev.iconEditor,
            colors: newColors,
          },
        };
      });
    }; // adding a new color to the iconColors array

    const toggleDeleteIconColorMode = () => {
      setDeleteIconColorMode(!deleteIconColorMode);
    }; // toggling the delete icon color mode state

    const commonIconSettings = useMemo(
      () =>
        computeCommonSettings({
          keycapData,
          entriesKey: 'icons',
          selectedKeycaps,
          selectedIndex: selectedIconIndex,
          selectedTag,
          fieldPaths: ICON_FIELD_PATHS,
          defaultObject: defaultIconObject,
        }),
      [
        keycapData,
        selectedKeycaps,
        selectedIconIndex,
        defaultIconObject,
        selectedTag,
      ]
    ); // the common value or MIXED of every setting across the target icon entries; null while a single icon entry, or none, is being edited (see utils/multiSelection.js)

    const isCommon = (value) => value != null && value !== MIXED; // small helper function to check if a value is common

    useEffect(() => {
      if (!commonIconSettings) return; // only run the following code when the icon settings apply to potentially multiple icon entries

      const width = getByPath(commonIconSettings, 'style.width', undefined); // get the value of style.width from the commonIconSettings

      if (isCommon(width)) {
        // if the width is common across all target icon entries:
        setTempWidth(width); // set the width in the UI to the common value
      } else {
        // if the with is not common across all target icon entries → clear everything UI-side:
        setTempWidth('__MIXED__');
      }

      const height = getByPath(commonIconSettings, 'style.height', undefined); // get the value of style.height from the commonIconSettings

      if (isCommon(height)) {
        // if the height is common across all target icon entries:
        setTempHeight(height); // set the height in the UI to the common value
      } else {
        // if the height is not common across all target icon entries → clear everything UI-side:
        setTempHeight('__MIXED__');
      }
    }, [commonIconSettings]); // this useEffect updates the width and height temp UI states based on the commonIconSettings (when the settings apply to potentially multiple icon entries at once)

    function resolveSetting(options) {
      return resolveSettingValue({
        commonSettings: commonIconSettings,
        selectedObject: selectedIconObject,
        ...options,
      });
    } // the value a control shows for a setting ({ path, defaultValue, mixedReturn }): the common value across the target icon entries, or the selected icon entry's

    const cellIsActive = (row, col) => {
      if (commonIconSettings) {
        // if commonIconSettings is true (not null), it means that the the user has selected one or more keycaps that hold at least one icon entry, but has not selected a specific icon entry (so the settings will be applied to all icon entries of all selected keycaps)
        const r = commonIconSettings?.position?.gridRow;
        const c = commonIconSettings?.position?.gridColumn;

        if (r === MIXED || c === MIXED) return false; // if the icon entries have mixed values for the gridRow or gridColumn → no active cell

        return (
          r === row &&
          c === col &&
          !isMixed(commonIconSettings, 'position.gridStructure')
        ); // if the icon entries share the common value for the gridRow, gridColumn and gridStructure → active cell
      }

      // if no keycap is selected, or the user has a specific icon entry selected → use selectedIconObject:
      return (
        selectedIconObject?.position?.gridRow === row &&
        selectedIconObject?.position?.gridColumn === col
      );
    }; // this helper function checks if a cell should be displayed as active in the UI

    const gridStructure = resolveSetting({
      path: 'position.gridStructure',
      defaultValue: '3x3',
      mixedReturn: undefined,
    });

    const cellAlignment = resolveSetting({
      path: 'position.cellAlignment',
      defaultValue: 'center',
      mixedReturn: '',
    });

    const mixedAspectRatioTitle = isMixed(
      commonIconSettings,
      'isActiveStates.preserveAspectRatio'
    )
      ? t('icon-editor-component-mixed-aspect-ratio')
      : undefined; // the tooltip of the size fields and the aspect-ratio toggle while the target icons disagree on keeping the ratio, and none otherwise (`false` as a title made React warn in development until PR 30)

    function createIconColorChangeHandler({
      setting, // setting: one of 'fill' | 'stroke'
      settingPath, // settingPath: the path where the direct color lives, e.g.: 'style.fill' or 'style.stroke'
    }) {
      return (color) => {
        const updatedColor = rgbaToHex(color.rgb); // convert color from the color picker to hex

        // 1: Handle the case when an icon color entry is selected (selectedColorEntry): → update colors array/list + all references inside the selectedIconColorIndexes object of all selected icon entries:
        updateDesign((prev) => {
          const idx = prev.iconEditor?.selectedColorEntry; // get the index of the selected icon color entry
          const colors = prev.iconEditor?.colors || []; // get the colors array/list of the icon editor

          if (idx == null || idx < 0 || idx >= colors.length) {
            return prev; // if no color entry is selected -> skip to the handleKeycapChange at the bottom of this function
          }

          // update the colors array/list:
          const nextColors = [...colors];
          nextColors[idx] = updatedColor;

          // update every icon entry that references this color entry index in ANY setting (fill or stroke):
          const nextKeycapData = prev.keycapData.map((row) =>
            row.map((keycap) => {
              // go through all keycaps of keycapData:
              if (!keycap || keycap.isSpacer || !keycap.icons) return keycap; // skip invalid keycaps

              const updatedIcons = Object.entries(keycap.icons).reduce(
                // go through all icon entries of the current keycap:
                (acc, [k, iconObj]) => {
                  if (!supportsSvgIconSettings(iconObj)) {
                    acc[k] = iconObj;
                    return acc; // skip PNG icons for fill/stroke color updates
                  }

                  const refs = iconObj.selectedIconColorIndexes || {}; // get the selectedIconColorIndexes object from the current icon entry
                  const refsThisIdx = refs.fill === idx || refs.stroke === idx; // check if any settings of the current icon entry reference the selected color entry from the colors array/list

                  if (!refsThisIdx) {
                    acc[k] = iconObj;
                    return acc; // if no settings of the current icon entry reference the selected color, don't update the current icon entry
                  }

                  const nextIcon = { ...iconObj };

                  if (refs.fill === idx) {
                    nextIcon.style = {
                      ...(nextIcon.style || {}),
                      fill: updatedColor, // if the fill settings of the current icon entry references the selected color entry, update its color
                    };
                  }
                  if (refs.stroke === idx) {
                    nextIcon.style = {
                      ...(nextIcon.style || {}),
                      stroke: updatedColor, // if the stroke settings of the current icon entry references the selected color entry, update its color
                    };
                  }

                  acc[k] = nextIcon;
                  return acc;
                },
                {} // initialize accumulator as an empty object for storing updated icons
              );

              return { ...keycap, icons: updatedIcons }; // return the updated keycap with modified icons
            })
          );

          return {
            ...prev,
            iconEditor: { ...prev.iconEditor, colors: nextColors },
            keycapData: nextKeycapData,
          };
        });

        // 2. If no color entry was selected:
        const positionForSingle =
          selectedTag != null ? null : (selectedIconIndex ?? null); // if the user has selected a tag, use null (because no icon entry can be selected while having a tag selected)

        handleKeycapChange(
          'icons',
          {
            [settingPath]: updatedColor, // apply the color directly to the settingPath of all target icon entries
            [`selectedIconColorIndexes.${setting}`]: null, // remove all references to a color entry for the setting
          },
          positionForSingle
        );
      };
    }

    const handleIconColorButtonClick = (index, keycapSetting, setting) => {
      if (deleteIconColorMode) {
        // 1. Handle color deletion:
        updateDesign((prev) => {
          const deletedColorIndex = index;
          const prevSelectedEntry = prev.iconEditor?.selectedColorEntry ?? null;
          const prevColors = prev.iconEditor?.colors ?? [];

          const updatedColors = prevColors.filter(
            (_, i) => i !== deletedColorIndex
          ); // remove the color at the index

          const updatedKeycapData = prev.keycapData.map((row) =>
            row.map((keycap) => {
              // go through all keycaps of keycapData
              if (!keycap || keycap.isSpacer || !keycap.icons) return keycap; // skip invalid keycaps/objects

              const icons = keycap.icons;

              const next = {};
              let changed = false;
              for (const [k, icon] of Object.entries(icons)) {
                // go through all icon entries of the current keycap:
                if (!icon) {
                  next[k] = icon;
                  continue;
                }
                const refs = { ...(icon.selectedIconColorIndexes || {}) }; // get the selectedIconColorIndexes from the current icon entry
                let touched = false;

                for (const refKey of Object.keys(refs)) {
                  // go through all color references of the current icon entry:
                  const v = refs[refKey];
                  if (v == null) continue; // if the current reference is null -> go to next
                  if (v === deletedColorIndex) {
                    refs[refKey] = null; // reset index to null if it matches the deleted one
                    touched = true;
                  } else if (v > deletedColorIndex) {
                    refs[refKey] = v - 1; // adjust index if it is greater than the deleted one
                    touched = true;
                  }
                }

                next[k] = touched
                  ? { ...icon, selectedIconColorIndexes: refs }
                  : icon;
                if (touched) changed = true;
              }

              return changed ? { ...keycap, icons: next } : keycap;
            })
          );

          // update the selectedColorEntry:
          const nextSelectedEntry =
            prevSelectedEntry == null
              ? null
              : prevSelectedEntry === deletedColorIndex
                ? null // reset the selectedColorEntry to null, if the color entry gets deleted
                : prevSelectedEntry > deletedColorIndex
                  ? prevSelectedEntry - 1 // if the selectedColorEntry comes after the deleted entry (inside the color list), subtract 1 from the selectedColorEntry index
                  : prevSelectedEntry;

          if (updatedColors.length === 0) toggleDeleteIconColorMode(); // auto-exit delete mode if the color array/list becomes empty

          return {
            ...prev,
            iconEditor: {
              ...prev.iconEditor,
              colors: updatedColors,
              selectedColorEntry: nextSelectedEntry,
            },
            keycapData: updatedKeycapData,
          };
        });
        return;
      }

      // 2. Handle selecting/deselecting an icon entry:
      if (isControlDisabled) {
        // 2.1 if there are no target icon entries -> toggle selectedColorEntry (UI selection):
        updateDesign((prev) => {
          const curr = prev.iconEditor?.selectedColorEntry ?? null; // current selectedColorEntry
          const next = curr === index ? null : index; // toggling the selected entry

          return {
            ...prev,
            iconEditor: { ...prev.iconEditor, selectedColorEntry: next },
          };
        });
        return;
      }

      // 2.2 If the user has selected keycaps or a tag, modify the target icon entries accordingly
      // build a flat list of targeted/selected icon entries as coordinates: { rIndex, kIndex, iconKey }:
      const targets = [];
      if (selectedTag != null) {
        // if the user has selected a tag -> collect all icon entries that reference the selected tag:
        keycapData.forEach((row, rIndex) => {
          row.forEach((kc, kIndex) => {
            // go through all keycaps:
            if (!kc || kc.isSpacer || !kc.icons) return;
            const icons = kc.icons;

            for (const [key, iconObj] of Object.entries(icons)) {
              // go through all icon entries of the current keycap:
              if (iconObj && iconObj.tagId === selectedTag) {
                targets.push({ rIndex, kIndex, iconKey: key }); // if the current icon entry references the selected tag -> add it to the targets array
              }
            }
          });
        });
      } else {
        // if the user has not selected a tag -> collect all icon entries of all selected keycaps (or the single selected icon entry):
        const selectedSet = new Set(
          selectedKeycaps.map(
            ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
          )
        ); // build a fast lookup of selected keycaps ("row:keycap" -> selected)
        const oneKeycap = selectedKeycaps.length === 1; // check if the user has exactly one keycap selected

        keycapData.forEach((row, rIndex) => {
          row.forEach((kc, kIndex) => {
            // go through all keycaps of keycapData
            if (!kc || kc.isSpacer || !selectedSet.has(`${rIndex}:${kIndex}`))
              return; // skip non-selected and invalid keycaps/objects

            const icons = kc.icons || {};
            if (oneKeycap && selectedIconIndex != null) {
              // if the user has exactly one keycap and icon entry selected:
              const key = String(selectedIconIndex);
              if (icons[key]) targets.push({ rIndex, kIndex, iconKey: key }); // add only this one icon entry to the targets array
            } else {
              // if the user has not selected a specific icon entry:
              for (const key of Object.keys(icons))
                targets.push({ rIndex, kIndex, iconKey: key }); // add all icon entries of this keycap to the targets array
            }
          });
        });
      }

      const svgCompatibleTargets = targets.filter(
        ({ rIndex, kIndex, iconKey }) => {
          const iconObj = keycapData?.[rIndex]?.[kIndex]?.icons?.[iconKey];
          return supportsSvgIconSettings(iconObj);
        }
      ); // filter out PNG icons (only SVG-compatible icons can receive fill/stroke color settings)

      if (svgCompatibleTargets.length === 0) return; // if no selected icon entries -> nothing to do

      const allSelectedAlreadyIndex = svgCompatibleTargets.every(
        ({ rIndex, kIndex, iconKey }) => {
          const icon = keycapData[rIndex][kIndex].icons[iconKey];
          const links = icon.selectedIconColorIndexes || {};
          return links[setting] === index;
        }
      ); // check if all target icon entries already reference the selected color entry

      if (allSelectedAlreadyIndex) {
        // if all selected icon entries reference the selected color entry:
        handleKeycapChange(
          'icons',
          { [`selectedIconColorIndexes.${setting}`]: null }, // reset the color reference of this setting to null
          selectedTag != null ? null : selectedIconIndex
        );
      } else {
        // if not all (or no) selected icon entries reference the selected color entry:
        const selectedColor = colors[index];
        if (selectedColor == null) return; // safety

        handleKeycapChange(
          'icons',
          {
            [`selectedIconColorIndexes.${setting}`]: index, // update the color reference for the setting of the current icon entry to the selected color index
            [keycapSetting]: selectedColor, // apply the selected color to the setting of the current icon entry
          },
          selectedTag != null ? null : selectedIconIndex
        );
      }
    }; // this function handles selecting, deselecting and deleting icon colors

    const majorityIconColors = useMemo(() => {
      const targets = iconSettingTargets.filter(supportsSvgIconSettings); // without the PNG icons, which have no fill or stroke

      return {
        fill: pickMajorityColor(
          targets,
          'style.fill',
          defaultIconObject,
          '#000000'
        ),
        stroke: pickMajorityColor(
          targets,
          'style.stroke',
          defaultIconObject,
          '#000000'
        ),
      };
    }, [iconSettingTargets, defaultIconObject]); // the colour each colour button shows: the one most target icon entries use (the fallback with no target)

    const uniformIconColorInfo = useMemo(
      () =>
        uniformColorInfo(
          iconSettingTargets.filter(supportsSvgIconSettings),
          ICON_COLOR_SETTINGS,
          'selectedIconColorIndexes'
        ),
      [iconSettingTargets]
    ); // per colour setting, whether the target icon entries (or the single selected one), PNG icons left out, reference the same colour of the colour list

    const showUnselectButton =
      selectedEditor === 'icons' &&
      (selectedKeycaps.length || selectedTag != null);

    const unselectKeycaps = () => {
      updateDesign({
        'settings.selectedKeycaps': [],
        'settings.isoEnterSelected': false,
        'textEditor.selectedTextIndex': null,
        'textEditor.selectedTag': null,
        'iconEditor.selectedIconIndex': null,
        'iconEditor.selectedTag': null,
      });
    }; // unselect all keycaps, tags, selected text and icon entry

    const round2 = (n) => parseFloat(Math.max(0.01, Number(n)).toFixed(2)); // helper function to ensure a value does not fall below the minimum threshold (0.01) and round to two decimal places. Not orderTotals' round2: this one floors at the smallest size an icon keeps and rounds through toFixed, the dimension fields' rule, left in the editor with its other input rules (chain 10, Oct 2026)
    const isNum = (v) => Number.isFinite(v); // helper function to check if a value is a number (not NaN, +Infinity or -Infinity)

    const createIconDimensionBlur = (setting) => (e) => {
      if (isMixed(commonIconSettings, 'isActiveStates.preserveAspectRatio'))
        return; // block when the 'isActiveStates.preserveAspectRatio' setting is mixed across the selected icons

      const raw = e.currentTarget?.value ?? e.target.value; // get the user input
      const typed = isNum(e.currentTarget?.valueAsNumber)
        ? e.currentTarget.valueAsNumber
        : parseFloat(raw); // try to get the users input as a number
      const inputVal = isNaN(typed) ? 10 : typed; // fallback to 10, if the users input is not a valid number
      const newPrimaryVal = round2(inputVal); // round the value to two decimal places and make sure it is >= 0.01

      const preserveAR = resolveSetting({
        path: 'isActiveStates.preserveAspectRatio',
        defaultValue: true,
        mixedReturn: true,
      }); // get the value of the 'isActiveStates.preserveAspectRatio' setting for the current icon entries selection (guaranteed non-mixed because we returned early above)

      updateDesign((prev) => {
        const selectedKeycaps = prev.settings?.selectedKeycaps || [];
        const selectedTag = prev.iconEditor?.selectedTag ?? null;
        const selIconIdx = prev.iconEditor?.selectedIconIndex ?? null; // get the index of the selected icon entry (if it exists)
        const oneKeycap = selectedKeycaps.length === 1; // check if the user has exactly one keycap selected

        const selectedSet = new Set(
          selectedKeycaps.map(
            ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
          )
        ); // create a fast lookup for all selected keycaps ("row:keycap")

        const tagMode = selectedTag != null;
        if (!tagMode && selectedKeycaps.length === 0) return prev; // if no keycaps and tag selected -> nothing to update

        const allNewW = [];
        const allNewH = [];

        const nextKeycapData = prev.keycapData.map((row, rIndex) =>
          row.map((keycap, kIndex) => {
            // go through all keycaps:
            if (!keycap || keycap.isSpacer) {
              return keycap; // skip invalid and spacer keycaps
            }

            if (!tagMode && !selectedSet.has(`${rIndex}:${kIndex}`))
              return keycap; // in tag mode we consider every keycap; otherwise only selected ones

            const icons = keycap.icons || {};
            let changed = false;

            const nextIcons = Object.fromEntries(
              Object.entries(icons).map(([iKey, iconObj]) => {
                // go through all icon entries of the current keycap:
                if (!iconObj) return [iKey, iconObj]; // skip invalid icon entries

                // decide if the current icon entry is a target:
                const isTarget = tagMode // if the user has selected a tag
                  ? iconObj.tagId === selectedTag // and the tagId of the icon entry matches the selected tag -> it is a target
                  : oneKeycap && selIconIdx != null // if the user has selected exactly one keycap and a specific icon entry
                    ? iKey === String(selIconIdx) // and the index of the current icon entry is the same as the selectedIconIndex -> it is a target
                    : true; // otherwise all icon entries of all selected keycaps are targets

                if (!isTarget) return [iKey, iconObj]; // skip non-target icon entries

                const curW = parseFloat(iconObj.style?.width) || 10; // get the current width of the icon
                const curH = parseFloat(iconObj.style?.height) || 10; // get the current height of the icon

                let newW = curW;
                let newH = curH;

                if (setting === 'width') {
                  // if the user edits the width:
                  newW = newPrimaryVal;

                  if (preserveAR) {
                    // adjust the height according to the new width if the preserve aspect ratio setting is active:
                    const ratio = newW / (curW || 1); // calculate the ratio of change in width
                    newH = round2(curH * ratio); // apply the same ratio to height to maintain aspect ratio (round the value to two decimal places and make sure it is >= 0.01)
                  } else {
                    newH = round2(curH); // if the preserve aspect ratio setting is not active, keep each icon's own height
                  }
                } else {
                  // if the user edits the height:
                  newH = newPrimaryVal;

                  if (preserveAR) {
                    // adjust the width according to the new height if the preserve aspect ratio setting is active:
                    const ratio = newH / (curH || 1); // calculate the ratio of change in height
                    newW = round2(curW * ratio); // apply the same ratio to width to maintain aspect ratio (round the value to two decimal places and make sure it is >= 0.01)
                  } else {
                    newW = round2(curW); // if the preserve aspect ratio setting is not active, keep each icon's own width
                  }
                }

                // add the new (or unchanged) width and height values of all selected icon entries to the allNewW and allNewH arrays:
                allNewW.push(newW);
                allNewH.push(newH);

                changed = true;
                return [
                  iKey,
                  {
                    ...iconObj,
                    style: {
                      ...(iconObj.style || {}),
                      width: newW, // update the current icon with the new width
                      height: newH, // update the current icon with the new height
                    },
                  },
                ];
              })
            );

            return changed ? { ...keycap, icons: nextIcons } : keycap;
          })
        );

        const wUniform =
          allNewW.length > 0 && allNewW.every((v) => v === allNewW[0]); // check if all new (or unchanged) width values of all selected icon entries are the same
        const hUniform =
          allNewH.length > 0 && allNewH.every((v) => v === allNewH[0]); // check if all new (or unchanged) height values of all selected icon entries are the same

        setTempWidth(wUniform ? String(allNewW[0]) : ''); // if all width values of all selected icon entries are the same, update this value to the UI. If they are mixed, don't show a value inside the input ('').
        setTempHeight(hUniform ? String(allNewH[0]) : ''); // if all height values of all selected icon entries are the same, update this value to the UI. If they are mixed, don't show a value inside the input ('').

        return { ...prev, keycapData: nextKeycapData };
      });
    };

    function createFlipClick(direction) {
      return () => {
        updateDesign((prev) => {
          const selectedKeycaps = prev.settings?.selectedKeycaps || [];
          const selectedTag = prev.iconEditor?.selectedTag ?? null;
          const selIconIdx = prev.iconEditor?.selectedIconIndex ?? null; // get the index of the selectde icon (if the user has selected a specific icon)
          const oneKeycap = selectedKeycaps.length === 1; // check if the user has exactly one keycap selected

          const tagMode = selectedTag != null;
          const selSet = new Set(
            selectedKeycaps.map(
              ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
            )
          ); // build a fast lookup for selected keycaps ("row:keycap")

          if (!tagMode && selectedKeycaps.length === 0) return prev; // if no tag selected and no keycaps selected -> nothing to do

          const nextKeycapData = prev.keycapData.map((row, rIndex) =>
            row.map((keycap, kIndex) => {
              // go through all keycaps:
              if (!keycap || keycap.isSpacer) return keycap; // skip invaid and spacer objects

              if (!tagMode && !selSet.has(`${rIndex}:${kIndex}`)) return keycap; // in tag mode we consider every keycap; otherwise only selected ones

              const icons = keycap.icons || {};
              let changed = false;

              const nextIcons = Object.fromEntries(
                Object.entries(icons).map(([iKey, iconObj]) => {
                  // go through all icon entries of the current keycap:
                  if (!iconObj) return [iKey, iconObj]; // skip invalid objects

                  // decide whether this icon is a target:
                  const isTarget = tagMode // if the user has selected a tag
                    ? iconObj.tagId === selectedTag // and the tagId of the icon entry matches the selected tag -> it is a target
                    : oneKeycap && selIconIdx != null // if the user has selected exactly one keycap and a specific icon entry
                      ? iKey === String(selIconIdx) // and the index of the current icon entry is the same as the selectedIconIndex -> it is a target
                      : true; // otherwise all icon entries of all selected keycaps are targets

                  if (!isTarget) return [iKey, iconObj]; // skip the current icon entry if it is not a target

                  const curFlip = iconObj.style?.flip || 'none';
                  const newFlip = toggleFlipValue(curFlip, direction);
                  if (newFlip === curFlip) return [iKey, iconObj]; // no change

                  changed = true;
                  return [
                    iKey,
                    {
                      ...iconObj,
                      style: {
                        ...(iconObj.style || {}),
                        flip: newFlip, // update the flip value of the current icon entry
                      },
                    },
                  ];
                })
              );

              return changed ? { ...keycap, icons: nextIcons } : keycap;
            })
          );

          return { ...prev, keycapData: nextKeycapData };
        });
      };
    } // factory function that returns an onClick handler that toggles the clicked flip value for all target icon entries

    const getTagColor = (id) =>
      predefinedTagColors?.[Math.max(0, (id || 1) - 1)] || '#ccc'; // helper function to get the color of a tag based on the tag id

    const handleSelectTag = (tagId) => {
      updateDesign((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          selectedKeycaps: [], // unselect all keycaps
        },
        iconEditor: {
          ...prev.iconEditor,
          selectedColorEntry: null, // unselect color entry
          selectedTag: tagId, // set chosen tag
          selectedIconIndex: null, // unselect specific icon entry
        },
      }));

      setShowTagDropdown(false); // hide the dropdown
    }; // select a tag for the Icon Editor

    const handleClearSelectedEditorTag = () => {
      updateDesign({ 'iconEditor.selectedTag': null }); // unselect the selected tag
      setShowTagDropdown(false); // hide the dropdown
    }; // clear the selected tag in the Icon Editor

    const hasIsoEnterIconForSelectedTag = useMemo(
      () =>
        collectTargetEntries({
          keycapData,
          entriesKey: 'icons',
          selectedTag,
          isoEnterOnly: true,
        }).length > 0,
      [keycapData, selectedTag]
    ); // whether an icon entry of an ISO Enter keycap references the selected tag

    function handleRenameTag(index, newName) {
      const prevTagsSnapshot = tags || []; // use current snapshot for validation
      if (index < 0 || index >= prevTagsSnapshot.length) {
        return { ok: false }; // invalid index
      }

      const { ok, reason, name } = validateTagRename(
        prevTagsSnapshot,
        index,
        newName
      ); // validate the new name
      if (!ok) {
        // if validation failed
        toast(t(TAG_NAME_KEYS[reason])); // inform the user
        return { ok: false };
      }

      if (name === (prevTagsSnapshot[index]?.name ?? '')) {
        return { ok: true, name }; // no design update needed (still report success, and echo normalized name)
      }

      updateDesign((prev) => {
        const prevTags = prev.iconEditor?.tags || [];
        if (index < 0 || index >= prevTags.length) return prev; // invalid index

        const nextTags = prevTags.map((tag, i) =>
          i === index ? { ...tag, name } : tag
        ); // update the name

        return {
          ...prev,
          iconEditor: {
            ...prev.iconEditor,
            tags: nextTags,
          },
        };
      });

      return { ok: true, name };
    } // commit a tag rename

    const handleIconLibraryTabSelect = (key) => {
      updateDesign({
        'iconEditor.activeIconLibraryTab': key || 'built-in',
      });
    }; // stores the selected icon library tab in the design state

    const uploadUserIconHandler = async (file) => {
      if (!userInfo) {
        toast.error(t('icon-editor-component-sign-in-to-upload-icons'));
        return; // require a signed-in user because uploaded icons are stored per account
      }

      if (!file) return;

      const allowedTypes = ['image/png', 'image/svg+xml']; // keep the client-side check aligned with the backend-supported upload formats

      if (!allowedTypes.includes(file.type)) {
        toast.error(t('icon-editor-component-upload-svg-or-png-file'));
        return;
      }

      const formData = new FormData();
      formData.append('icon', file); // send the file under the field name expected by the backend upload middleware

      try {
        setIsUploadingUserIcon(true);

        const { data } = await api.post('/api/user-icons', formData);

        await fetchUserIconPreviews(); // refresh the uploaded icon library after the backend has stored the new icon

        toast.success(
          data?.message
            ? t(getError({ response: { data } }))
            : t('icon-editor-component-icon-upload-success')
        );
      } catch (err) {
        const errorMessage = getError(err);
        const errorDetails = err?.response?.data?.details; // backend validation errors may include interpolation values for the translated message

        toast.error(
          errorMessage
            ? t(errorMessage, { details: errorDetails })
            : t('icon-editor-component-icon-upload-failed')
        );
      } finally {
        setIsUploadingUserIcon(false);
      }
    }; // handles uploading a user icon

    const deleteUserIconHandler = async (userIcon) => {
      if (!userIcon?._id || deletingUserIconId) return; // only an icon the account may delete gets here: the library's button is disabled for the others (a guard with a toast for them was dead, deleted Oct 2026); one sent anyway reaches the API, which answers 404 for an icon the account doesn't own, and the catch below shows that

      const deletedAssetId = String(userIcon._id); // keep one stable string id for API calls and local cleanup comparisons

      try {
        setDeletingUserIconId(deletedAssetId);

        const iconIsUsedInCart =
          await isUserIconUsedInIndexedDBCartDesigns(deletedAssetId); // check whether any cart design in IndexedDB still references the uploaded icon

        if (iconIsUsedInCart) {
          toast.info(t('icon-editor-component-icon-used-in-cart'));
          return; // block deletion so cart designs and their thumbnails do not become inconsistent
        }

        const { data } = await api.delete(`/api/user-icons/${deletedAssetId}`);

        removeDeletedUserIconFromCurrentDesign(deletedAssetId); // remove the deleted icon from current design state and localStorage
        removeDeletedUserIconFromHistoryStacks(deletedAssetId); // remove the deleted icon from undoStack and redoStack

        setUserIconPreviews((previousUserIcons) =>
          previousUserIcons.filter(
            (icon) => String(icon._id) !== deletedAssetId
          )
        ); // remove the deleted icon from the uploaded icon library UI

        setUserIconAssetsById((previousAssetsById) => {
          const nextAssetsById = { ...previousAssetsById };
          delete nextAssetsById[deletedAssetId];
          return nextAssetsById;
        }); // remove cached render/export asset data for the deleted icon

        toast.success(
          data?.message
            ? t(getError({ response: { data } }))
            : t('icon-editor-component-icon-delete-success')
        );
      } catch (err) {
        const errorMessage = getError(err);
        const errorDetails = err?.response?.data?.details; // backend validation errors may include interpolation values for the translated message

        toast.error(
          errorMessage
            ? t(errorMessage, { details: errorDetails })
            : t('icon-editor-component-icon-delete-failed')
        );
      } finally {
        setDeletingUserIconId(null);
      }
    }; // handles deleting a user icon

    const selectedPngAsset = useMemo(() => {
      if (!selectedIconIsPng || !normalizedSelectedIcon.assetId) {
        return null;
      }

      return userIconAssetsById[String(normalizedSelectedIcon.assetId)] || null;
    }, [selectedIconIsPng, normalizedSelectedIcon.assetId, userIconAssetsById]); // gets the cached uploaded PNG asset for the selected icon

    const selectedPngDpiQuality = useMemo(() => {
      const shouldShowPngDpiQuality =
        exactlyOneSelected && // check if the user has exactly one keycap selected
        selectedIconIndex != null && // check if the user has a specific icon selected
        selectedIconIsPng && // check if the selected icon is a .png
        selectedPngAsset && // make sure the asset (png file) exists
        !isControlDisabled; // make sure icon settings are enabled

      if (!shouldShowPngDpiQuality) {
        return null; // don't display the dpi quality badge
      }

      return calculatePngDpiQuality({
        widthPx: selectedPngAsset.widthPx,
        heightPx: selectedPngAsset.heightPx,
        widthMm: tempWidth || normalizedSelectedIcon.style?.width,
        heightMm: tempHeight || normalizedSelectedIcon.style?.height,
      });
    }, [
      exactlyOneSelected,
      selectedIconIndex,
      selectedIconIsPng,
      selectedPngAsset,
      isControlDisabled,
      tempWidth,
      tempHeight,
      normalizedSelectedIcon.style?.width,
      normalizedSelectedIcon.style?.height,
    ]); // gets the DPI quality info for the selected uploaded PNG icon

    if (!isActive) {
      return null; // Do not render anything if not active. (this is better than conditionally rendering the IconEditor component in the CustomizerScreen, because now this component doesn't mound and unmount every time the user switches tabs. This cause the useEffect hooks to be triggered for every mount and the states to be set back to defaults whenever it is unmounted)
    }

    return (
      <div id="icon-editor" className="icon-editor-container">
        <Modal
          show={showTagManager}
          onHide={() => {
            setShowTagManager(false);
            setSelectedTagPosition(null);
          }}
          className="tag-modal"
        >
          <Modal.Header>
            <Row className="w-100">
              <Col xs={2}></Col>
              <Col xs={8}>
                <Modal.Title>
                  {t('icon-editor-component-manage-tags-modal-title')}
                </Modal.Title>
              </Col>
              <Col
                xs={2}
                className="d-flex justify-content-end align-items-center"
              >
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowTagManager(false);
                    setSelectedTagPosition(null);
                  }}
                  aria-label="Close"
                ></button>
              </Col>
            </Row>
          </Modal.Header>

          <Modal.Body>
            <Form.Group controlId="createTag">
              <Form.Control
                type="text"
                placeholder={t(
                  'icon-editor-component-manage-tags-modal-tag-name'
                )}
                className="new-tag-input customizer-mobile-device-compatible-top"
                onChange={(e) => setNewTagName(e.target.value)}
              />

              {/* Display color options as selectable buttons */}
              <div className="tag-colors-container">
                {predefinedTagColors.map((color, index) => {
                  const isColorInUse = tags.some((tag) => tag.id === index + 1); // Check if color (by index) is already assigned

                  return (
                    <button
                      key={index}
                      type="button"
                      className={`tag-color ${
                        newTagColor === color
                          ? 'selected-tag-color'
                          : 'default-tag-color'
                      } ${
                        isColorInUse
                          ? 'tag-color-in-use'
                          : 'tag-color-not-in-use'
                      }`}
                      onClick={() => setNewTagColor(color)}
                      disabled={isColorInUse} // Only allow selection if color is not in use
                      aria-pressed={newTagColor === color}
                      aria-label={`${t(
                        'icon-editor-component-tag-color'
                      )} ${index + 1}`}
                      style={{
                        backgroundColor: color,
                        border:
                          newTagColor === color
                            ? '2px solid black'
                            : `2px solid ${color}`,
                      }}
                    >
                      {/* Overlay a checkmark if the color is already in use */}
                      {isColorInUse && <span>✓</span>}
                    </button>
                  );
                })}
              </div>

              <Button
                className="add-tag-button"
                onClick={() => createTag(newTagName, newTagColor)}
              >
                {t('icon-editor-component-manage-tags-modal-add-tag')}
              </Button>

              <div className="tag-list-container">
                {tags.map((tag, index) => (
                  <div
                    key={index}
                    className={`tag-container ${
                      selectedTagId === tag.id ? 'selected-tag-container' : ''
                    }`}
                  >
                    <svg
                      fill="currentColor"
                      className="tag-modal-delete-svg"
                      viewBox="0 0 56 56"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => deleteTag(index)}
                      cursor="pointer"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path d="M 44.5235 48.6602 L 46.1407 14.3945 L 48.4844 14.3945 C 49.4454 14.3945 50.2187 13.5976 50.2187 12.6367 C 50.2187 11.6758 49.4454 10.8555 48.4844 10.8555 L 38.2422 10.8555 L 38.2422 7.3398 C 38.2422 3.9883 35.9688 1.8086 32.3595 1.8086 L 23.5938 1.8086 C 19.9844 1.8086 17.7344 3.9883 17.7344 7.3398 L 17.7344 10.8555 L 7.5391 10.8555 C 6.6016 10.8555 5.7813 11.6758 5.7813 12.6367 C 5.7813 13.5976 6.6016 14.3945 7.5391 14.3945 L 9.8829 14.3945 L 11.5000 48.6836 C 11.6641 52.0586 13.8907 54.1914 17.2657 54.1914 L 38.7579 54.1914 C 42.1095 54.1914 44.3595 52.0351 44.5235 48.6602 Z M 21.4844 7.5742 C 21.4844 6.2383 22.4688 5.3008 23.8751 5.3008 L 32.1016 5.3008 C 33.5313 5.3008 34.5157 6.2383 34.5157 7.5742 L 34.5157 10.8555 L 21.4844 10.8555 Z M 17.6173 50.6758 C 16.2579 50.6758 15.2500 49.6445 15.1797 48.2852 L 13.5391 14.3945 L 42.3907 14.3945 L 40.8438 48.2852 C 40.7735 49.6680 39.7891 50.6758 38.4063 50.6758 Z M 34.9610 46.5508 C 35.7344 46.5508 36.3204 45.9180 36.3438 45.0273 L 37.0469 20.2773 C 37.0704 19.3867 36.4610 18.7305 35.6641 18.7305 C 34.9376 18.7305 34.3282 19.4102 34.3048 20.2539 L 33.6016 45.0273 C 33.5782 45.8711 34.1641 46.5508 34.9610 46.5508 Z M 21.0626 46.5508 C 21.8595 46.5508 22.4454 45.8711 22.4219 45.0273 L 21.7188 20.2539 C 21.6954 19.4102 21.0626 18.7305 20.3360 18.7305 C 19.5391 18.7305 18.9532 19.3867 18.9766 20.2773 L 19.7032 45.0273 C 19.7266 45.9180 20.2891 46.5508 21.0626 46.5508 Z M 29.4298 45.0273 L 29.4298 20.2539 C 29.4298 19.4102 28.7969 18.7305 28.0235 18.7305 C 27.2500 18.7305 26.5938 19.4102 26.5938 20.2539 L 26.5938 45.0273 C 26.5938 45.8711 27.2500 46.5508 28.0235 46.5508 C 28.7735 46.5508 29.4298 45.8711 29.4298 45.0273 Z"></path>
                      </g>
                    </svg>

                    <div className="w-100">
                      <TagNameField
                        key={tag.id}
                        tag={tag}
                        index={index}
                        onCommit={handleRenameTag}
                      />
                    </div>

                    {selectedTagId === tag.id && hoveredIndex === index ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        className="tag-modal-tag-svg"
                        cursor="pointer"
                        fill={predefinedTagColors[tag.id - 1]} // Access the color based on tag.id
                        onClick={() => {
                          if (
                            !Array.isArray(selectedKeycaps) ||
                            selectedKeycaps.length !== 1
                          )
                            return; // must have exactly one keycap selected

                          if (selectedTagPosition == null) return; // selectedTagPosition must be a valid position

                          const { rowIndex, keycapIndex } = selectedKeycaps[0];
                          const kc = keycapData?.[rowIndex]?.[keycapIndex];
                          if (!kc || kc.isSpacer) return;

                          const icons = kc.icons;
                          const key = String(selectedTagPosition);
                          const hasEntry = !!icons?.[key];
                          if (!hasEntry) return; // make sure the selected keycap has an icon entry at the selectedTagPosition position

                          handleKeycapChange(
                            'icons',
                            {
                              tagId: null,
                            },
                            selectedTagPosition
                          );
                        }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        <path
                          fillRule="evenodd"
                          d="M6.146 5.146a.5.5 0 0 1 .708 0L8 6.293l1.146-1.147a.5.5 0 1 1 .708.708L8.707 7l1.147 1.146a.5.5 0 0 1-.708.708L8 7.707 6.854 8.854a.5.5 0 1 1-.708-.708L7.293 7 6.146 5.854a.5.5 0 0 1 0-.708"
                        />
                        <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z" />
                      </svg>
                    ) : selectedTagId === tag.id && hoveredIndex !== index ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        className="tag-modal-tag-svg"
                        fill={predefinedTagColors[tag.id - 1]} // Access the color based on tag.id
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        <path
                          fillRule="evenodd"
                          d="M2 15.5V2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.74.439L8 13.069l-5.26 2.87A.5.5 0 0 1 2 15.5m8.854-9.646a.5.5 0 0 0-.708-.708L7.5 7.793 6.354 6.646a.5.5 0 1 0-.708.708l1.5 1.5a.5.5 0 0 0 .708 0z"
                        />
                      </svg>
                    ) : selectedTagId !== tag.id && hoveredIndex === index ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        className="tag-modal-tag-svg"
                        cursor="pointer"
                        fill={predefinedTagColors[tag.id - 1]} // Access the color based on tag.id
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onClick={() => {
                          if (
                            !Array.isArray(selectedKeycaps) ||
                            selectedKeycaps.length !== 1
                          )
                            return; // must have exactly one keycap selected

                          if (selectedTagPosition == null) return; // selectedTagPosition must be a valid position

                          const { rowIndex, keycapIndex } = selectedKeycaps[0];
                          const kc = keycapData?.[rowIndex]?.[keycapIndex];
                          if (!kc || kc.isSpacer) return;

                          const icons = kc.icons;
                          const key = String(selectedTagPosition);
                          const hasEntry = !!icons?.[key];
                          if (!hasEntry) return; // make sure the selected keycap has an icon entry at the selectedTagPosition position

                          handleKeycapChange(
                            'icons',
                            {
                              tagId: tag.id,
                            },
                            selectedTagPosition
                          );
                        }}
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0"
                        />
                        <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z" />
                      </svg>
                    ) : selectedTagId !== tag.id && hoveredIndex !== index ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        className="tag-modal-tag-svg"
                        fill={predefinedTagColors[tag.id - 1]} // Access the color based on tag.id
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        <path d="M2 2v13.5a.5.5 0 0 0 .74.439L8 13.069l5.26 2.87A.5.5 0 0 0 14 15.5V2a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2" />
                      </svg>
                    ) : null}
                  </div>
                ))}
              </div>
            </Form.Group>
          </Modal.Body>
        </Modal>

        <div className="icon-entries-and-icon-settings-wrapper">
          {/* click-away to unselect, not a control of its own — keyboard users press Escape instead (see the effect above), so the interaction rules are deliberately off here: */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            id="icon-entries"
            className={`icon-entries-container`}
            onClick={(e) => {
              if (selectedIconIndex != null) {
                updateDesign({ 'iconEditor.selectedIconIndex': null }); // if the user clicks somewhere inside the div (apart from the elements that use e.stopPropagation() inside their onClick), unselect the icon entry
              }
            }}
          >
            <div
              className={`icon-entries-heading-container ${
                !exactlyOneSelected ||
                !keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                  singleSelectedKeycap?.keycapIndex
                ]?.icons
                  ? 'disabled-icon-entries-heading-container'
                  : ''
              }`}
            >
              <h3>{t('icon-editor-component-icon-entries')}</h3>

              {screenWidthMD && (
                <Button
                  variant="link"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the icon-entries-container div)

                    setShowIconEntries(!showIconEntries);
                  }}
                  aria-expanded={showIconEntries}
                  className="toggle-settings-arrow"
                >
                  {showIconEntries ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-caret-up-fill"
                      viewBox="0 0 16 16"
                    >
                      <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-caret-down-fill"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                  )}
                </Button>
              )}
            </div>

            {(screenWidthMD && showIconEntries) || !screenWidthMD ? (
              <div
                className={`icon-entries-wrapper ${
                  keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                    singleSelectedKeycap?.keycapIndex
                  ]?.icons &&
                  Object.keys(
                    keycapData[singleSelectedKeycap?.rowIndex][
                      singleSelectedKeycap?.keycapIndex
                    ].icons
                  ).length > 0
                    ? 'non-empty-icon-entries-wrapper'
                    : 'empty-icon-entries-wrapper'
                }`}
              >
                {/* Ensure icons is defined and not empty: */}
                {exactlyOneSelected &&
                  keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                    singleSelectedKeycap?.keycapIndex
                  ]?.icons &&
                  Object.keys(
                    keycapData[singleSelectedKeycap?.rowIndex][
                      singleSelectedKeycap?.keycapIndex
                    ].icons
                  ).length > 0 && (
                    <div className="icon-entries-grid">
                      {/* Map through icons: */}
                      {Object.entries(
                        keycapData[singleSelectedKeycap?.rowIndex][
                          singleSelectedKeycap?.keycapIndex
                        ].icons
                      ).map(([position, iconObj]) => {
                        const normalizedIcon = normalizeIconObject(iconObj); // adds missing default icon fields so older saved Iconify icons and new uploaded icons can be handled with one shape
                        const { tagId, visible = true } = normalizedIcon;

                        return (
                          <div
                            key={position}
                            className={`icon-entry-container ${
                              selectedIconIndex === position
                                ? 'selected-icon-entry-container'
                                : ''
                            }`}
                          >
                            <div className="icon-entry-top-row">
                              <button
                                type="button"
                                className="a11y-unstyled-icon-button"
                                aria-label={t(
                                  'icon-editor-component-delete-icon-entry'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevents the entries container's onClick from firing

                                  handleKeycapChange(
                                    'icons',
                                    {},
                                    null,
                                    false,
                                    position
                                  );
                                }}
                              >
                                <svg
                                  fill="currentColor"
                                  className="icon-entry-trash-svg"
                                  viewBox="0 0 56 56"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                  <g
                                    id="SVGRepo_tracerCarrier"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  ></g>
                                  <g id="SVGRepo_iconCarrier">
                                    <path d="M 44.5235 48.6602 L 46.1407 14.3945 L 48.4844 14.3945 C 49.4454 14.3945 50.2187 13.5976 50.2187 12.6367 C 50.2187 11.6758 49.4454 10.8555 48.4844 10.8555 L 38.2422 10.8555 L 38.2422 7.3398 C 38.2422 3.9883 35.9688 1.8086 32.3595 1.8086 L 23.5938 1.8086 C 19.9844 1.8086 17.7344 3.9883 17.7344 7.3398 L 17.7344 10.8555 L 7.5391 10.8555 C 6.6016 10.8555 5.7813 11.6758 5.7813 12.6367 C 5.7813 13.5976 6.6016 14.3945 7.5391 14.3945 L 9.8829 14.3945 L 11.5000 48.6836 C 11.6641 52.0586 13.8907 54.1914 17.2657 54.1914 L 38.7579 54.1914 C 42.1095 54.1914 44.3595 52.0351 44.5235 48.6602 Z M 21.4844 7.5742 C 21.4844 6.2383 22.4688 5.3008 23.8751 5.3008 L 32.1016 5.3008 C 33.5313 5.3008 34.5157 6.2383 34.5157 7.5742 L 34.5157 10.8555 L 21.4844 10.8555 Z M 17.6173 50.6758 C 16.2579 50.6758 15.2500 49.6445 15.1797 48.2852 L 13.5391 14.3945 L 42.3907 14.3945 L 40.8438 48.2852 C 40.7735 49.6680 39.7891 50.6758 38.4063 50.6758 Z M 34.9610 46.5508 C 35.7344 46.5508 36.3204 45.9180 36.3438 45.0273 L 37.0469 20.2773 C 37.0704 19.3867 36.4610 18.7305 35.6641 18.7305 C 34.9376 18.7305 34.3282 19.4102 34.3048 20.2539 L 33.6016 45.0273 C 33.5782 45.8711 34.1641 46.5508 34.9610 46.5508 Z M 21.0626 46.5508 C 21.8595 46.5508 22.4454 45.8711 22.4219 45.0273 L 21.7188 20.2539 C 21.6954 19.4102 21.0626 18.7305 20.3360 18.7305 C 19.5391 18.7305 18.9532 19.3867 18.9766 20.2773 L 19.7032 45.0273 C 19.7266 45.9180 20.2891 46.5508 21.0626 46.5508 Z M 29.4298 45.0273 L 29.4298 20.2539 C 29.4298 19.4102 28.7969 18.7305 28.0235 18.7305 C 27.2500 18.7305 26.5938 19.4102 26.5938 20.2539 L 26.5938 45.0273 C 26.5938 45.8711 27.2500 46.5508 28.0235 46.5508 C 28.7735 46.5508 29.4298 45.8711 29.4298 45.0273 Z"></path>
                                  </g>
                                </svg>
                              </button>

                              <button
                                type="button"
                                className="a11y-unstyled-icon-button"
                                aria-label={t(
                                  visible
                                    ? 'icon-editor-component-hide-icon-entry'
                                    : 'icon-editor-component-show-icon-entry'
                                )}
                                aria-pressed={!visible}
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevents the entries container's onClick from firing

                                  handleKeycapChange(
                                    'icons',
                                    { visible: !visible },
                                    position
                                  );
                                }} // Toggle visibility when clicked
                              >
                                {visible ? (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="black"
                                    className="icon-entry-eye-svg"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                                    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                                  </svg>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="icon-entry-eye-svg"
                                    fill="grey"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7 7 0 0 0-2.79.588l.77.771A6 6 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755q-.247.248-.517.486z" />
                                    <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829" />
                                    <path d="M3.35 5.47q-.27.24-.518.487A13 13 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7 7 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12z" />
                                  </svg>
                                )}
                              </button>

                              <button
                                type="button"
                                className="a11y-unstyled-icon-button"
                                aria-label={t(
                                  'icon-editor-component-tag-icon-entry'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevents the entries container's onClick from firing

                                  setShowTagManager(true);
                                  setSelectedTagPosition(position);
                                }}
                              >
                                {tagId ? (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill={predefinedTagColors[tagId - 1]}
                                    className="icon-entry-bookmark-svg"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M2 2v13.5a.5.5 0 0 0 .74.439L8 13.069l5.26 2.87A.5.5 0 0 0 14 15.5V2a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2" />
                                  </svg>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="currentColor"
                                    className="icon-entry-bookmark-svg"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z" />
                                    <path d="M8 4a.5.5 0 0 1 .5.5V6H10a.5.5 0 0 1 0 1H8.5v1.5a.5.5 0 0 1-1 0V7H6a.5.5 0 0 1 0-1h1.5V4.5A.5.5 0 0 1 8 4" />
                                  </svg>
                                )}
                              </button>
                            </div>

                            {/* the preview doubles as the entry's select button, so selecting an entry works by keyboard without nesting the buttons of the top row inside it: */}
                            <button
                              type="button"
                              className="a11y-unstyled-button icon-entry-svg-container"
                              aria-label={t(
                                'icon-editor-component-select-icon-entry'
                              )}
                              aria-pressed={selectedIconIndex === position}
                              onClick={(e) => {
                                e.stopPropagation(); // prevent bubble to container (so that when the user triggers this onClick, it does not also trigger the onClick of the icon-entries-container div)

                                if (selectedIconIndex !== position) {
                                  handleSelectIconEntry(position);
                                }
                              }}
                            >
                              <IconEntryPreview
                                userInfo={userInfo}
                                iconObj={normalizedIcon}
                                userIconPreviewsById={userIconPreviewsById}
                                refreshAllRelevantUserIconPreviewsOnce={
                                  refreshAllRelevantUserIconPreviewsOnce
                                }
                              />
                            </button>
                          </div>
                        );
                      })}

                      {exactlyOneSelected &&
                      keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                        singleSelectedKeycap?.keycapIndex
                      ]?.icons &&
                      Object.keys(
                        keycapData[singleSelectedKeycap?.rowIndex][
                          singleSelectedKeycap?.keycapIndex
                        ].icons
                      ).length > 0 &&
                      Object.keys(
                        keycapData[singleSelectedKeycap?.rowIndex][
                          singleSelectedKeycap?.keycapIndex
                        ].icons
                      ).length < 10 ? (
                        <Button
                          variant="none"
                          className="small-add-icon-entry-button"
                          disabled={
                            Object.keys(
                              keycapData[singleSelectedKeycap?.rowIndex][
                                singleSelectedKeycap?.keycapIndex
                              ].icons
                            ).length >= 10
                          }
                          onClick={(e) => {
                            e.stopPropagation(); // Prevents the outer div's onClick from firing

                            handleKeycapChange('icons', {}, null, true);
                          }}
                        >
                          +
                        </Button>
                      ) : null}
                    </div>
                  )}

                {exactlyOneSelected &&
                keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                  singleSelectedKeycap?.keycapIndex
                ]?.icons &&
                Object.keys(
                  keycapData[singleSelectedKeycap?.rowIndex][
                    singleSelectedKeycap?.keycapIndex
                  ].icons
                ).length === 0 ? (
                  <Button
                    variant="none"
                    className="big-add-icon-entry-button"
                    disabled={
                      Object.keys(
                        keycapData[singleSelectedKeycap?.rowIndex][
                          singleSelectedKeycap?.keycapIndex
                        ].icons
                      ).length >= 10
                    }
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents the outer div's onClick from firing

                      handleKeycapChange('icons', {}, null, true);
                    }}
                  >
                    +
                  </Button>
                ) : !exactlyOneSelected ||
                  !keycapData?.[singleSelectedKeycap?.rowIndex]?.[
                    singleSelectedKeycap?.keycapIndex
                  ]?.icons ? (
                  <div className="select-a-keycap-message">
                    {t('icon-editor-component-select-a-keycap-message')}
                  </div>
                ) : null}
              </div>
            ) : (
              <div></div>
            )}
          </div>

          <div className="icon-entry-settings-container">
            <div ref={tagDropdownRef} className="tag-container-icon-settings">
              <svg
                id="icon-settings-tag"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                onClick={() => {
                  if (tags.length > 0) {
                    setShowTagDropdown((s) => !s);
                  } else {
                    toast(t('icon-editor-component-no-tags-available'));
                  }
                }}
                role="button"
                aria-label="Choose tag"
              >
                <path
                  fill={selectedTag ? getTagColor(selectedTag) : '#fff'}
                  d="M2 2v13.5a.5.5 0 0 0 .74.439L8 13.069l5.26 2.87A.5.5 0 0 0 14 15.5V2a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2"
                />
                <path
                  fill={selectedTag ? getTagColor(selectedTag) : '#1a1a1a'}
                  d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z"
                />
              </svg>

              {showTagDropdown && (
                <Dropdown.Menu ref={menuRef} show className="tag-dropdown-menu">
                  {tags.map((tag) => {
                    const isCurrent = tag.id === selectedTag;
                    const currentTagColor = getTagColor(tag.id);

                    return (
                      <Dropdown.Item
                        key={tag.id}
                        active={isCurrent}
                        onClick={() => {
                          if (isCurrent) {
                            handleClearSelectedEditorTag();
                          } else {
                            handleSelectTag(tag.id);
                          }
                        }}
                        title={tag.name}
                      >
                        <span>{tag.name}</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill={currentTagColor}
                          viewBox="0 0 16 16"
                        >
                          <path d="M2 2v13.5a.5.5 0 0 0 .74.439L8 13.069l5.26 2.87A.5.5 0 0 0 14 15.5V2a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2" />
                        </svg>
                      </Dropdown.Item>
                    );
                  })}

                  {selectedTag && (
                    <>
                      <Dropdown.Divider />

                      <Dropdown.Item
                        className="remove-tag-dropdown-item"
                        onClick={handleClearSelectedEditorTag}
                      >
                        {t('icon-editor-component-unselect-tag')}
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              )}
            </div>

            {screenWidthXL && (
              <div className={`settings-heading-container`}>
                <h3>{t('icon-editor-component-settings-heading')}</h3>

                {screenWidthMD && (
                  <Button
                    variant="link"
                    onClick={() => setShowIconSettings(!showIconSettings)}
                    aria-expanded={showIconSettings}
                    className="toggle-settings-arrow"
                  >
                    {showIconSettings ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-caret-up-fill"
                        viewBox="0 0 16 16"
                      >
                        <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-caret-down-fill"
                        viewBox="0 0 16 16"
                      >
                        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                      </svg>
                    )}
                  </Button>
                )}
              </div>
            )}

            <div>
              {(screenWidthMD && showIconSettings) || !screenWidthMD ? (
                <div>
                  <div id="icon-entry-position">
                    {screenWidthMD && (
                      <div
                        className={`position-heading-container ${
                          isControlDisabled
                            ? 'disabled-icon-entry-settings'
                            : ''
                        }`}
                      >
                        <h4 className="position-heading">
                          {t('icon-editor-component-position')}
                        </h4>

                        <Button
                          variant="link"
                          onClick={() =>
                            setShowPositionSettings(!showPositionSettings)
                          }
                          aria-expanded={showPositionSettings}
                          className="toggle-settings-arrow"
                        >
                          {showPositionSettings ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-caret-up-fill"
                              viewBox="0 0 16 16"
                            >
                              <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-caret-down-fill"
                              viewBox="0 0 16 16"
                            >
                              <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                            </svg>
                          )}
                        </Button>
                      </div>
                    )}

                    {(screenWidthMD && showPositionSettings) ||
                    !screenWidthMD ? (
                      <div
                        className={`${
                          isControlDisabled
                            ? 'disabled-icon-entry-settings'
                            : ''
                        }`}
                      >
                        {/* ISO Enter Section Settings */}
                        {isoEnterSelected || hasIsoEnterIconForSelectedTag ? (
                          <div className="iso-enter-settings-container">
                            <Form.Label>
                              {t('icon-editor-component-iso-enter-section')}

                              <OverlayTrigger
                                trigger="click"
                                rootClose // close the popover when the user clicks outside of it
                                placement="bottom"
                                overlay={
                                  <Popover>
                                    <Popover.Header as="h3">
                                      {t(
                                        'icon-editor-component-iso-enter-section'
                                      )}
                                    </Popover.Header>
                                    <Popover.Body>
                                      {t(
                                        'icon-editor-component-iso-enter-section-popover-1'
                                      )}
                                      <br />
                                      <br />-{' '}
                                      <strong>
                                        {t(
                                          'icon-editor-component-iso-enter-section-popover-2'
                                        )}
                                      </strong>
                                      {t(
                                        'icon-editor-component-iso-enter-section-popover-3'
                                      )}
                                      <br />
                                      <br />-{' '}
                                      <strong>
                                        {t(
                                          'icon-editor-component-iso-enter-section-popover-4'
                                        )}
                                      </strong>
                                      {t(
                                        'icon-editor-component-iso-enter-section-popover-5'
                                      )}
                                    </Popover.Body>
                                  </Popover>
                                }
                              >
                                <Button className="info-popover">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="#707070"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                  </svg>
                                </Button>
                              </OverlayTrigger>
                            </Form.Label>

                            <div className="w-100">
                              <Form.Control
                                as="select"
                                className="customizer-mobile-device-compatible-top"
                                value={resolveSetting({
                                  path: 'position.isoSection',
                                  defaultValue: 'top',
                                  mixedReturn: '__MIXED__',
                                })}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '__MIXED__') return; // never pass the UI mixedReturn placeholder to the handleKeycapChange function (value is guaranteed to be 'top' or 'main')

                                  handleKeycapChange(
                                    'icons',
                                    {
                                      'position.isoSection': value,
                                    },
                                    selectedIconIndex
                                  );
                                }}
                              >
                                {/* epmty placeholder for MIXED: */}
                                <option value="__MIXED__" disabled></option>

                                <option value="top">
                                  {t('icon-editor-component-top')}
                                </option>
                                <option value="main">
                                  {t('icon-editor-component-main')}
                                </option>
                              </Form.Control>
                            </div>
                          </div>
                        ) : null}

                        {/* Grid Structure */}
                        <div className="grid-container">
                          <Nav
                            justify
                            variant="tabs"
                            activeKey={gridStructure}
                            onSelect={(eventKey) => {
                              handleKeycapChange(
                                'icons',
                                { 'position.gridStructure': eventKey },
                                selectedIconIndex
                              );
                            }}
                          >
                            <Nav.Item
                              title={t('icon-editor-component-3x3-grid')}
                            >
                              <Nav.Link eventKey="3x3">
                                <Grid3x3 />
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item
                              title={t('icon-editor-component-3x2-grid')}
                            >
                              <Nav.Link eventKey="3x2">
                                <Grid3x2 />
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item
                              title={t('icon-editor-component-2x3-grid')}
                            >
                              <Nav.Link eventKey="2x3">
                                <Grid2x3 />
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item
                              title={t('icon-editor-component-2x2-grid')}
                            >
                              <Nav.Link eventKey="2x2">
                                <Grid2x2 />
                              </Nav.Link>
                            </Nav.Item>
                          </Nav>
                        </div>
                      </div>
                    ) : (
                      <div></div>
                    )}

                    <div
                      className={`${
                        isControlDisabled ? 'disabled-icon-entry-settings' : ''
                      }`}
                    >
                      {!screenWidthMD && (
                        <div className="position-heading-container">
                          <h4 className="position-heading">
                            {t('icon-editor-component-position')}
                          </h4>

                          <Button
                            variant="link"
                            onClick={() =>
                              setShowPositionSettings(!showPositionSettings)
                            }
                            aria-expanded={showPositionSettings}
                            className="toggle-settings-arrow"
                          >
                            {showPositionSettings ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-caret-up-fill"
                                viewBox="0 0 16 16"
                              >
                                <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-caret-down-fill"
                                viewBox="0 0 16 16"
                              >
                                <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                              </svg>
                            )}
                          </Button>
                        </div>
                      )}

                      {showPositionSettings && (
                        <div className="position-settings-container">
                          <div
                            className="cell-container"
                            title={
                              getByPath(
                                commonIconSettings,
                                'position.gridStructure',
                                undefined
                              ) === MIXED
                                ? t(
                                    'icon-editor-component-select-a-cell-disabled'
                                  )
                                : t('icon-editor-component-select-a-cell')
                            }
                          >
                            <Form.Label>
                              {t('icon-editor-component-cell')}
                            </Form.Label>

                            {gridStructure === '3x3' ||
                            isMixed(
                              commonIconSettings,
                              'position.gridStructure'
                            ) ? (
                              <svg
                                className={
                                  getByPath(
                                    commonIconSettings,
                                    'position.gridStructure',
                                    undefined
                                  ) === MIXED
                                    ? 'disabled-setting'
                                    : ''
                                }
                                viewBox="0 0 600 600"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g id="layer1">
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 1)
                                        ? '#000'
                                        : '#fff',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectCell(1, 1)}
                                    id="rect1"
                                    width="170.00705"
                                    height="170.00705"
                                    x="14.996477"
                                    y="14.996477"
                                    rx="13.710619"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 2)
                                        ? '#000'
                                        : '#fff',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectCell(1, 2)}
                                    id="rect1-5"
                                    width="170.00705"
                                    height="170.00705"
                                    x="-385.00354"
                                    y="-185.00351"
                                    rx="13.710619"
                                    transform="scale(-1)"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 3)
                                        ? '#000'
                                        : '#fff',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectCell(1, 3)}
                                    id="rect1-4"
                                    width="170.00705"
                                    height="170.00705"
                                    x="414.99646"
                                    y="14.996477"
                                    rx="13.710619"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 1)
                                        ? '#000'
                                        : '#fff',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectCell(2, 1)}
                                    id="rect1-8"
                                    width="170.00705"
                                    height="170.00705"
                                    x="14.996477"
                                    y="214.99649"
                                    rx="13.710619"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 2)
                                        ? '#000'
                                        : '#fff',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectCell(2, 2)}
                                    id="rect1-5-7"
                                    width="170.00705"
                                    height="170.00705"
                                    x="214.99649"
                                    y="214.99649"
                                    rx="13.710619"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 3)
                                        ? '#000'
                                        : '#fff',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectCell(2, 3)}
                                    id="rect1-4-7"
                                    width="170.00705"
                                    height="170.00705"
                                    x="414.99649"
                                    y="214.99648"
                                    rx="13.710618"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(3, 1)
                                        ? '#000'
                                        : '#fff',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectCell(3, 1)}
                                    id="rect1-0"
                                    width="170.00705"
                                    height="170.00705"
                                    x="14.996477"
                                    y="414.99646"
                                    rx="13.710619"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(3, 2)
                                        ? '#000'
                                        : '#fff',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectCell(3, 2)}
                                    id="rect1-5-74"
                                    width="170.00705"
                                    height="170.00705"
                                    x="214.99648"
                                    y="414.99649"
                                    rx="13.710619"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(3, 3)
                                        ? '#000'
                                        : '#fff',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleSelectCell(3, 3)}
                                    id="rect1-4-8"
                                    width="170.12692"
                                    height="170.12694"
                                    x="414.93655"
                                    y="414.93655"
                                    rx="13.720286"
                                  />
                                </g>
                              </svg>
                            ) : gridStructure === '3x2' ? (
                              <svg
                                viewBox="0 0 600 600"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g id="layer1">
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 1)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(1, 1)}
                                    id="rect1"
                                    width="269.88684"
                                    height="169.88683"
                                    x="15.056577"
                                    y="15.056577"
                                    rx="21.765659"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 2)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(1, 2)}
                                    id="rect1-5"
                                    width="269.88684"
                                    height="169.88684"
                                    x="-584.94342"
                                    y="-184.94341"
                                    rx="21.765659"
                                    transform="scale(-1)"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 1)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(2, 1)}
                                    id="rect1-8"
                                    width="269.88684"
                                    height="169.88684"
                                    x="15.056575"
                                    y="215.05658"
                                    rx="21.765659"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 2)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(2, 2)}
                                    id="rect1-5-7"
                                    width="269.88684"
                                    height="169.88684"
                                    x="315.05655"
                                    y="215.05658"
                                    rx="21.765659"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(3, 1)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(3, 1)}
                                    id="rect1-0"
                                    width="269.88684"
                                    height="169.88684"
                                    x="15.056575"
                                    y="415.05655"
                                    rx="21.765659"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(3, 2)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(3, 2)}
                                    id="rect1-5-74"
                                    width="269.88684"
                                    height="169.88684"
                                    x="315.05655"
                                    y="415.05655"
                                    rx="21.765659"
                                  />
                                </g>
                              </svg>
                            ) : gridStructure === '2x3' ? (
                              <svg
                                viewBox="0 0 600 600"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g id="layer1">
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 1)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(1, 1)}
                                    id="rect1"
                                    width="167.55452"
                                    height="267.55447"
                                    x="16.222738"
                                    y="16.222738"
                                    rx="13.51283"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 2)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(1, 2)}
                                    id="rect1-5"
                                    width="167.55453"
                                    height="267.55453"
                                    x="-383.77728"
                                    y="-283.77725"
                                    rx="13.51283"
                                    transform="scale(-1)"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 3)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(1, 3)}
                                    id="rect1-4"
                                    width="167.55453"
                                    height="267.55453"
                                    x="416.22272"
                                    y="16.222734"
                                    rx="13.51283"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 1)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(2, 1)}
                                    id="rect1-8"
                                    width="167.55453"
                                    height="267.55453"
                                    x="16.222734"
                                    y="316.22275"
                                    rx="13.51283"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 2)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(2, 2)}
                                    id="rect1-5-7"
                                    width="167.55453"
                                    height="267.55453"
                                    x="216.22275"
                                    y="316.22275"
                                    rx="13.51283"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 3)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(2, 3)}
                                    id="rect1-4-7"
                                    width="167.55453"
                                    height="267.55453"
                                    x="416.22275"
                                    y="316.22275"
                                    rx="13.512829"
                                  />
                                </g>
                              </svg>
                            ) : gridStructure === '2x2' ? (
                              <svg
                                viewBox="0 0 600 600"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g id="layer1">
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 1)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(1, 1)}
                                    id="rect1"
                                    width="269.80493"
                                    height="269.80487"
                                    x="15.097548"
                                    y="15.097548"
                                    rx="21.759048"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(1, 2)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(1, 2)}
                                    id="rect1-5"
                                    width="269.80493"
                                    height="269.80487"
                                    x="-584.9024"
                                    y="-284.9024"
                                    rx="21.759048"
                                    transform="scale(-1)"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 1)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(2, 1)}
                                    id="rect1-8"
                                    width="269.80493"
                                    height="269.80487"
                                    x="15.097548"
                                    y="315.09753"
                                    rx="21.759048"
                                  />
                                  <rect
                                    style={{
                                      fill: cellIsActive(2, 2)
                                        ? '#000'
                                        : '#fff',
                                      cursor: 'pointer',
                                      fillRule: 'evenodd',
                                      stroke: '#000000',
                                      strokeWidth: '20',
                                      strokeDasharray: 'none',
                                    }}
                                    onClick={() => handleSelectCell(2, 2)}
                                    id="rect1-5-7"
                                    width="269.80493"
                                    height="269.80487"
                                    x="315.09753"
                                    y="315.09753"
                                    rx="21.759048"
                                  />
                                </g>
                              </svg>
                            ) : null}
                          </div>

                          {/* Cell Alignment */}
                          <div
                            className="alignment-container"
                            title={t('icon-editor-component-align-icon')}
                          >
                            <Form.Label>
                              {t('icon-editor-component-alignment')}
                            </Form.Label>

                            <svg
                              viewBox="-15 -15 630 630"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                width="600"
                                height="600"
                                fill="none"
                                stroke="#000" // Apply stroke here
                                strokeWidth="20" // Apply strokeWidth here
                                rx="13.710619"
                              />
                              {/* Bottom Right */}
                              <rect
                                style={{
                                  fill:
                                    cellAlignment === 'bottomRight'
                                      ? 'black'
                                      : 'white',
                                  stroke:
                                    cellAlignment === 'bottomRight'
                                      ? 'black'
                                      : 'none',
                                  strokeWidth: '20',
                                  cursor: 'pointer',
                                }}
                                width="172.31422"
                                height="172.31421"
                                x="413.8429"
                                y="413.8429"
                                rx="13.896686"
                                onClick={() =>
                                  handleSelectCellAlignment('bottomRight')
                                }
                              />
                              {/* Bottom */}
                              <rect
                                style={{
                                  fill:
                                    cellAlignment === 'bottom'
                                      ? 'black'
                                      : 'white',
                                  stroke:
                                    cellAlignment === 'bottom'
                                      ? 'black'
                                      : 'none',
                                  strokeWidth: '20',
                                  cursor: 'pointer',
                                }}
                                width="172.31422"
                                height="172.31421"
                                x="213.84288"
                                y="413.8429"
                                rx="13.896686"
                                onClick={() =>
                                  handleSelectCellAlignment('bottom')
                                }
                              />
                              {/* Bottom Left */}
                              <rect
                                style={{
                                  fill:
                                    cellAlignment === 'bottomLeft'
                                      ? 'black'
                                      : 'white',
                                  stroke:
                                    cellAlignment === 'bottomLeft'
                                      ? 'black'
                                      : 'none',
                                  strokeWidth: '20',
                                  cursor: 'pointer',
                                }}
                                width="172.31422"
                                height="172.31421"
                                x="13.842896"
                                y="413.8429"
                                rx="13.896686"
                                onClick={() =>
                                  handleSelectCellAlignment('bottomLeft')
                                }
                              />
                              {/* Right */}
                              <rect
                                style={{
                                  fill:
                                    cellAlignment === 'right'
                                      ? 'black'
                                      : 'white',
                                  stroke:
                                    cellAlignment === 'right'
                                      ? 'black'
                                      : 'none',
                                  strokeWidth: '20',
                                  cursor: 'pointer',
                                }}
                                width="172.31422"
                                height="172.31421"
                                x="413.8429"
                                y="213.8429"
                                rx="13.896686"
                                onClick={() =>
                                  handleSelectCellAlignment('right')
                                }
                              />
                              {/* Center */}
                              <rect
                                style={{
                                  fill:
                                    cellAlignment === 'center'
                                      ? 'black'
                                      : 'white',
                                  stroke:
                                    cellAlignment === 'center'
                                      ? 'black'
                                      : 'none',
                                  strokeWidth: '20',
                                  cursor: 'pointer',
                                }}
                                width="172.31422"
                                height="172.31421"
                                x="213.8429"
                                y="213.8429"
                                rx="13.896686"
                                onClick={() =>
                                  handleSelectCellAlignment('center')
                                }
                              />
                              {/* Left */}
                              <rect
                                style={{
                                  fill:
                                    cellAlignment === 'left'
                                      ? 'black'
                                      : 'white',
                                  stroke:
                                    cellAlignment === 'left' ? 'black' : 'none',
                                  strokeWidth: '20',
                                  cursor: 'pointer',
                                }}
                                width="172.31422"
                                height="172.31421"
                                x="13.842896"
                                y="213.8429"
                                rx="13.896686"
                                onClick={() =>
                                  handleSelectCellAlignment('left')
                                }
                              />
                              {/* Top Right */}
                              <rect
                                style={{
                                  fill:
                                    cellAlignment === 'topRight'
                                      ? 'black'
                                      : 'white',
                                  stroke:
                                    cellAlignment === 'topRight'
                                      ? 'black'
                                      : 'none',
                                  strokeWidth: '20',
                                  cursor: 'pointer',
                                }}
                                width="172.31422"
                                height="172.31421"
                                x="413.8429"
                                y="13.842896"
                                rx="13.896686"
                                onClick={() =>
                                  handleSelectCellAlignment('topRight')
                                }
                              />
                              {/* Top */}
                              <rect
                                style={{
                                  fill:
                                    cellAlignment === 'top' ? 'black' : 'white',
                                  stroke:
                                    cellAlignment === 'top' ? 'black' : 'none',
                                  strokeWidth: '20',
                                  cursor: 'pointer',
                                }}
                                width="172.31422"
                                height="172.31421"
                                x="213.8429"
                                y="13.842896"
                                rx="13.896686"
                                onClick={() => handleSelectCellAlignment('top')}
                              />
                              {/* Top Left */}
                              <rect
                                style={{
                                  fill:
                                    cellAlignment === 'topLeft'
                                      ? 'black'
                                      : 'white',
                                  stroke:
                                    cellAlignment === 'topLeft'
                                      ? 'black'
                                      : 'none',
                                  strokeWidth: '20',
                                  cursor: 'pointer',
                                }}
                                width="172.31422"
                                height="172.31421"
                                x="13.842896"
                                y="13.842896"
                                rx="13.896686"
                                onClick={() =>
                                  handleSelectCellAlignment('topLeft')
                                }
                              />

                              {/* Static Paths (Lines and Circle) */}
                              <path
                                d="m 452.85954,452.85956 94.28092,94.28093 m 0,0 v -47.14048 m 0,47.14048 -47.14045,-2e-5"
                                strokeWidth="16.6667"
                                stroke={
                                  cellAlignment === 'bottomRight'
                                    ? 'white'
                                    : 'black'
                                }
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  handleSelectCellAlignment('bottomRight')
                                }
                              />
                              <path
                                d="m 300,433.33333 v 133.33334 m 0,0 33.33333,-33.33333 M 300,566.66667 266.66665,533.33334"
                                stroke={
                                  cellAlignment === 'bottom' ? 'white' : 'black'
                                }
                                strokeWidth="16.6667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  handleSelectCellAlignment('bottom')
                                }
                              />
                              <path
                                d="m 147.14048,452.85953 -94.280913,94.28093 m 0,0 47.140463,-10e-6 m -47.140463,10e-6 -1.4e-5,-47.14047"
                                stroke={
                                  cellAlignment === 'bottomLeft'
                                    ? 'white'
                                    : 'black'
                                }
                                strokeWidth="16.6667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  handleSelectCellAlignment('bottomLeft')
                                }
                              />
                              <path
                                d="m 433.33334,299.99999 h 133.33334 m 0,0 -33.33333,-33.33333 m 33.33333,33.33333 -33.33333,33.33334"
                                stroke={
                                  cellAlignment === 'right' ? 'white' : 'black'
                                }
                                strokeWidth="16.6667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  handleSelectCellAlignment('right')
                                }
                              />
                              <circle
                                style={{
                                  fill:
                                    cellAlignment === 'center'
                                      ? 'white'
                                      : 'black',
                                  stroke:
                                    cellAlignment === 'center'
                                      ? 'white'
                                      : 'black',
                                  strokeWidth: '3.84647',
                                  cursor: 'pointer',
                                }}
                                cx="300"
                                cy="300"
                                r="23.076765"
                                onClick={() =>
                                  handleSelectCellAlignment('center')
                                }
                              />
                              <path
                                d="M 166.66668,300 H 33.333332 m 0,0 33.33333,33.33333 M 33.333332,300 66.666662,266.66666"
                                stroke={
                                  cellAlignment === 'left' ? 'white' : 'black'
                                }
                                strokeWidth="16.6667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  handleSelectCellAlignment('left')
                                }
                              />
                              <path
                                d="m 452.85954,147.14045 94.28091,-94.280912 m 0,0 -47.14045,0 m 47.14045,0 10e-6,47.140461"
                                stroke={
                                  cellAlignment === 'topRight'
                                    ? 'white'
                                    : 'black'
                                }
                                strokeWidth="16.6667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  handleSelectCellAlignment('topRight')
                                }
                              />
                              <path
                                d="M 300,166.66667 V 33.333331 m 0,0 -33.33333,33.33333 M 300,33.333331 l 33.33335,33.33333"
                                stroke={
                                  cellAlignment === 'top' ? 'white' : 'black'
                                }
                                strokeWidth="16.6667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() => handleSelectCellAlignment('top')}
                              />
                              <path
                                d="M 147.14046,147.14046 52.859563,52.859565 m 0,0 -1.3e-5,47.140445 m 1.3e-5,-47.140445 47.140457,-7e-6"
                                stroke={
                                  cellAlignment === 'topLeft'
                                    ? 'white'
                                    : 'black'
                                }
                                strokeWidth="16.6667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  handleSelectCellAlignment('topLeft')
                                }
                              />
                            </svg>
                          </div>

                          {/* Manual Icon Positioning Settings */}
                          <div className="icon-position-settings-container">
                            <Row>
                              <Col
                                className={`bounding-box-container ${
                                  pngIncompatibleControlsDisabled
                                    ? 'disabled-icon-entry-settings'
                                    : ''
                                }`}
                              >
                                <Form.Label>
                                  {t('icon-editor-component-bounding-box')}

                                  <OverlayTrigger
                                    trigger="click"
                                    rootClose // close the popover when the user clicks outside of it
                                    placement="bottom"
                                    overlay={
                                      <Popover>
                                        <Popover.Header as="h3">
                                          {t(
                                            'icon-editor-component-bounding-box-modal-popover-1'
                                          )}
                                        </Popover.Header>
                                        <Popover.Body>
                                          {t(
                                            'icon-editor-component-bounding-box-modal-popover-2'
                                          )}{' '}
                                          <br />
                                          <br />-{' '}
                                          <strong>
                                            {t(
                                              'icon-editor-component-bounding-box-modal-popover-3'
                                            )}
                                          </strong>
                                          {t(
                                            'icon-editor-component-bounding-box-modal-popover-4'
                                          )}
                                          <br />
                                          <em
                                            style={{
                                              color: '#ff4d4f',
                                              fontSize: '0.9em',
                                            }}
                                          >
                                            {t(
                                              'icon-editor-component-bounding-box-modal-popover-5'
                                            )}
                                          </em>
                                          <br />
                                          <br />-{' '}
                                          <strong>
                                            {t(
                                              'icon-editor-component-bounding-box-modal-popover-6'
                                            )}
                                          </strong>
                                          {t(
                                            'icon-editor-component-bounding-box-modal-popover-7'
                                          )}
                                        </Popover.Body>
                                      </Popover>
                                    }
                                  >
                                    <Button className="info-popover">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="#707070"
                                        className="bi bi-info-circle"
                                        viewBox="0 0 16 16"
                                      >
                                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                      </svg>
                                    </Button>
                                  </OverlayTrigger>
                                </Form.Label>

                                <Form.Control
                                  as="select"
                                  className="customizer-mobile-device-compatible-top"
                                  value={resolveSetting({
                                    path: 'position.boundingBox',
                                    defaultValue: 'standard',
                                    mixedReturn: '__MIXED__',
                                  })}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '__MIXED__') return; // never pass the UI mixedReturn placeholder to the handleKeycapChange function (value is guaranteed to be 'compact' or 'standard')

                                    handleKeycapChange(
                                      'icons',
                                      {
                                        'position.boundingBox': value,
                                      },
                                      selectedIconIndex
                                    );
                                  }}
                                  style={{
                                    fontSize: '10px',
                                    padding: '3px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {/* epmty placeholder for MIXED: */}
                                  <option value="__MIXED__" disabled></option>

                                  <option value="compact">
                                    {t('icon-editor-component-compact')}
                                  </option>
                                  <option value="standard">
                                    {t('icon-editor-component-standard')}
                                  </option>
                                </Form.Control>
                              </Col>

                              <Col className="rotation-container">
                                <Form.Label>
                                  {t('icon-editor-component-rotation')}
                                </Form.Label>

                                <CustomInput
                                  position="top"
                                  value={resolveSetting({
                                    path: 'position.rotation',
                                    defaultValue: 0,
                                    mixedReturn: '__MIXED__',
                                  })}
                                  onChange={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    const rotation = Math.max(
                                      -360,
                                      Math.min(360, Number(e.target.value))
                                    );

                                    handleKeycapChange(
                                      'icons',
                                      { 'position.rotation': rotation },
                                      selectedIconIndex
                                    );
                                  }}
                                  onBlur={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    handleKeycapChange(
                                      'icons',
                                      {
                                        'position.rotation': parseFloat(
                                          parseFloat(e.target.value).toFixed(2)
                                        ), // Format to 2 decimal places and ensure it's a number
                                      },
                                      selectedIconIndex
                                    );
                                  }}
                                  unitSymbol="°"
                                  min="-360"
                                  max="360"
                                />
                              </Col>
                            </Row>

                            <Row>
                              <Col className="x-offset-container">
                                <Form.Label>
                                  {t('icon-editor-component-x-offset')}
                                </Form.Label>

                                <CustomInput
                                  position="top"
                                  value={resolveSetting({
                                    path: 'position.xOffset',
                                    defaultValue: 0,
                                    mixedReturn: '__MIXED__',
                                  })}
                                  onChange={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    handleKeycapChange(
                                      'icons',
                                      {
                                        'position.xOffset': Number(
                                          e.target.value
                                        ),
                                      }, // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                      selectedIconIndex
                                    );
                                  }} // Let users type freely
                                  onBlur={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    handleKeycapChange(
                                      'icons',
                                      {
                                        'position.xOffset': parseFloat(
                                          parseFloat(e.target.value).toFixed(2)
                                        ), // Format to 2 decimal places and ensure it's stored as a number
                                      },
                                      selectedIconIndex
                                    );
                                  }}
                                  unitSymbol="mm"
                                  step="0.1"
                                />
                              </Col>

                              <Col className="y-offset-container">
                                <Form.Label>
                                  {t('icon-editor-component-y-offset')}
                                </Form.Label>

                                <CustomInput
                                  position="top"
                                  value={resolveSetting({
                                    path: 'position.yOffset',
                                    defaultValue: 0,
                                    mixedReturn: '__MIXED__',
                                  })}
                                  onChange={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    handleKeycapChange(
                                      'icons',
                                      {
                                        'position.yOffset': Number(
                                          e.target.value
                                        ),
                                      }, // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                      selectedIconIndex
                                    );
                                  }} // Let users type freely
                                  onBlur={(e) => {
                                    const number =
                                      e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                    if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                    handleKeycapChange(
                                      'icons',
                                      {
                                        'position.yOffset': parseFloat(
                                          parseFloat(e.target.value).toFixed(2)
                                        ), // Format to 2 decimal places and ensure it's stored as a number
                                      },
                                      selectedIconIndex
                                    );
                                  }}
                                  unitSymbol="mm"
                                  step="0.1"
                                />
                              </Col>
                            </Row>
                          </div>
                        </div>
                      )}
                    </div>

                    {showPositionSettings && (
                      <div
                        className={`guidelines-settings-container ${
                          isControlDisabled
                            ? 'disabled-icon-entry-settings'
                            : ''
                        }`}
                      >
                        <div className="display-grid-container">
                          <Form.Check
                            type="checkbox"
                            label={t('icon-editor-component-grid')}
                            checked={showGrid}
                            onChange={() =>
                              updateDesign({ 'settings.showGrid': !showGrid })
                            }
                          />
                        </div>

                        <div className="display-bounding-box-container">
                          <Form.Check
                            type="checkbox"
                            label={t('icon-editor-component-bounding-box')}
                            checked={showBoundingBox}
                            onChange={() =>
                              updateDesign({
                                'settings.showBoundingBox': !showBoundingBox,
                              })
                            }
                          />
                        </div>

                        {/* Printable Area is currently disabled:
                          <div className="display-printable-area-container">
                            <Form.Check
                              type="checkbox"
                              label={t('icon-editor-component-printable-area')}
                              checked={showPrintableArea}
                              onChange={() =>
                                updateDesign({
                                  'settings.showPrintableArea':
                                    !showPrintableArea,
                                })
                              }
                            />
                          </div>
                        */}
                      </div>
                    )}
                  </div>

                  <div
                    className={`customizer-divider ${
                      isControlDisabled ? 'disabled-icon-entry-settings' : ''
                    }`}
                  ></div>

                  <div id="icon-entry-style">
                    <div className="icon-style-heading-container">
                      <h4>{t('icon-editor-component-style')}</h4>

                      <Button
                        variant="link"
                        onClick={() =>
                          setShowIconStyleSettings(!showIconStyleSettings)
                        }
                        aria-expanded={showIconStyleSettings}
                        className="toggle-settings-arrow"
                      >
                        {showIconStyleSettings ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-caret-up-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-caret-down-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                          </svg>
                        )}
                      </Button>
                    </div>

                    {showIconStyleSettings && (
                      <div className="icon-style-container">
                        {/* The following Button is only displayed on screens >= 576. On smaller screens it is placed after the icon-dimensions-container */}
                        {!pngIncompatibleControlsDisabled && (
                          <SplitButton
                            className={`d-none d-sm-flex ${
                              isControlDisabled ||
                              pngIncompatibleControlsDisabled
                                ? 'disabled-custom-split-button'
                                : 'custom-split-button'
                            } ${
                              resolveSetting({
                                path: 'isActiveStates.fill',
                                defaultValue: false,
                                mixedReturn: false,
                              })
                                ? 'custom-split-button-enabled'
                                : ''
                            }`}
                            variant="none"
                            title={
                              <svg
                                className="icon-color-svg"
                                viewBox="0 0 24 24"
                                version="1.1"
                                id="svg1"
                              >
                                <path
                                  fill="currentColor"
                                  d="M 8.65,18.5 2.5,12.35 Q 2.25,12.1 2.125,11.8 2,11.5 2,11.175 2,10.85 2.125,10.55 2.25,10.25 2.5,10 L 8.25,4.275 5.6,1.625 7.15,0 l 10,10 q 0.25,0.25 0.363,0.55 0.113,0.3 0.112,0.625 -0.001,0.325 -0.112,0.625 -0.111,0.3 -0.363,0.55 L 11,18.5 Q 10.75,18.75 10.45,18.875 10.15,19 9.825,19 9.5,19 9.2,18.875 8.9,18.75 8.65,18.5 M 9.825,5.85 4.475,11.2 h 10.7 z M 19.8,19 Q 18.9,19 18.275,18.362 17.65,17.724 17.65,16.8 q 0,-0.675 0.338,-1.275 0.338,-0.6 0.762,-1.175 L 19.8,13 l 1.1,1.35 q 0.4,0.575 0.75,1.175 0.35,0.6 0.35,1.275 0,0.925 -0.65,1.563 Q 20.7,19.001 19.8,19"
                                  id="path1"
                                />
                                <rect
                                  id="rect1"
                                  width="22.169491"
                                  height="3.4576271"
                                  x="0.91525459"
                                  y="20.542374"
                                  stroke={
                                    targets.length > 0 ||
                                    (exactlyOneSelected && selectedIconIndex)
                                      ? majorityIconColors.fill
                                      : selectedColorEntry !== null
                                        ? colors[selectedColorEntry]
                                        : '#000000'
                                  }
                                  fill={
                                    targets.length > 0 ||
                                    (exactlyOneSelected && selectedIconIndex)
                                      ? majorityIconColors.fill
                                      : selectedColorEntry !== null
                                        ? colors[selectedColorEntry]
                                        : '#000000'
                                  }
                                />
                              </svg>
                            }
                            drop="up-centered"
                            onClick={() => {
                              if (
                                isControlDisabled ||
                                pngIncompatibleControlsDisabled
                              )
                                return;

                              handleKeycapChange(
                                'icons',
                                {
                                  'isActiveStates.fill': !resolveSetting({
                                    path: 'isActiveStates.fill',
                                    defaultValue: false,
                                    mixedReturn: false,
                                  }),
                                },
                                selectedIconIndex
                              );
                            }}
                          >
                            <div className="d-flex justify-content-center mb-5">
                              <ChromePicker
                                className={`icon-editor-chrome-picker ${
                                  (isControlDisabled &&
                                    selectedColorEntry === null) ||
                                  (pngIncompatibleControlsDisabled &&
                                    selectedColorEntry === null)
                                    ? 'disabled-icon-entry-settings'
                                    : ''
                                }`}
                                color={
                                  targets.length > 0 ||
                                  (exactlyOneSelected && selectedIconIndex)
                                    ? majorityIconColors.fill
                                    : selectedColorEntry !== null
                                      ? colors[selectedColorEntry]
                                      : '#000000'
                                }
                                onChange={createIconColorChangeHandler({
                                  setting: 'fill',
                                  settingPath: 'style.fill',
                                })}
                              />
                            </div>

                            <div className="icon-color-color-picker-container">
                              <ColorList
                                colors={colors}
                                selectedIndex={selectedColorEntry}
                                handleColorButtonClick={
                                  handleIconColorButtonClick
                                }
                                handleAddColor={handleAddIconColor}
                                deleteMode={deleteIconColorMode}
                                toggleDeleteMode={toggleDeleteIconColorMode}
                                keycapSetting={'style.fill'}
                                setting={'fill'}
                                currentColor={majorityIconColors.fill}
                                hasSelection={
                                  uniformIconColorInfo.fill.hasSelection
                                }
                                isUniformSelection={
                                  uniformIconColorInfo.fill.isUniform
                                }
                                uniformSelectedIndex={
                                  uniformIconColorInfo.fill.uniformIndex
                                }
                                showUnselectButton={showUnselectButton}
                                unselectKeycaps={unselectKeycaps}
                              />
                            </div>
                          </SplitButton>
                        )}

                        <div
                          className={`icon-dimensions-container ${
                            isControlDisabled
                              ? 'disabled-icon-entry-settings'
                              : ''
                          }`}
                        >
                          <OverlayTrigger
                            trigger="click"
                            rootClose // close the popover when the user clicks outside of it
                            placement="top"
                            overlay={
                              <Popover className="icon-dimensions-popover">
                                <Popover.Header as="h3">
                                  {t(
                                    'icon-editor-component-icon-dimensions-popover-6'
                                  )}
                                </Popover.Header>
                                <Popover.Body>
                                  {t(
                                    'icon-editor-component-icon-dimensions-popover-5'
                                  )}
                                </Popover.Body>

                                <Popover.Header as="h3">
                                  {t(
                                    'icon-editor-component-icon-dimensions-popover-1'
                                  )}
                                </Popover.Header>
                                <Popover.Body>
                                  {t(
                                    'icon-editor-component-icon-dimensions-popover-2'
                                  )}
                                  <br />
                                  <br />
                                  {t(
                                    'icon-editor-component-icon-dimensions-popover-3'
                                  )}
                                  <br />
                                  <br />
                                  <em
                                    style={{
                                      fontSize: '0.9em',
                                    }}
                                  >
                                    {t(
                                      'icon-editor-component-icon-dimensions-popover-4'
                                    )}
                                  </em>
                                </Popover.Body>
                              </Popover>
                            }
                          >
                            <Button className="info-popover">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="#707070"
                                className="bi bi-info-circle"
                                viewBox="0 0 16 16"
                              >
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                              </svg>
                            </Button>
                          </OverlayTrigger>

                          <div
                            className="icon-width-container"
                            title={mixedAspectRatioTitle}
                          >
                            <Form.Label>
                              {t('icon-editor-component-width')}
                            </Form.Label>

                            <div
                              className={`${
                                isMixed(
                                  commonIconSettings,
                                  'isActiveStates.preserveAspectRatio'
                                )
                                  ? 'disabled-icon-entry-settings'
                                  : ''
                              }`}
                            >
                              <CustomInput
                                position="center"
                                value={
                                  isControlDisabled
                                    ? defaultStyle.width
                                    : tempWidth
                                } // Use temporary width state (or the default, when no keycap is selected)
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  setTempWidth(inputValue); // Update the temporary width state with the input value, allowing the UI to reflect user input dynamically.
                                }}
                                onBlur={createIconDimensionBlur('width')}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault(); // Prevent the default Enter key action
                                    e.target.blur(); // Manually trigger onBlur event
                                  }
                                }}
                                unitSymbol="mm"
                                step="0.1"
                              />
                            </div>
                          </div>

                          <Button
                            variant="none"
                            className="preserve-aspect-ratio-button no-css"
                            title={mixedAspectRatioTitle}
                            onClick={() => {
                              if (
                                isMixed(
                                  commonIconSettings,
                                  'isActiveStates.preserveAspectRatio'
                                ) &&
                                window.confirm(
                                  t(
                                    'icon-editor-component-apply-common-aspect-ratio'
                                  )
                                ) // ask the user if he really wants to apply a common aspect ratio setting to all icons of all selected keyaps
                              ) {
                                handleKeycapChange(
                                  'icons',
                                  {
                                    'isActiveStates.preserveAspectRatio':
                                      !resolveSetting({
                                        path: 'isActiveStates.preserveAspectRatio',
                                        defaultValue: false, // should be true, but we use ! in front of the resolveSetting call (false -> true)
                                        mixedReturn: false, // should be true, but we use ! in front of the resolveSetting call (false -> true)
                                      }),
                                  },
                                  selectedIconIndex
                                );
                              } else if (
                                !isMixed(
                                  commonIconSettings,
                                  'isActiveStates.preserveAspectRatio'
                                )
                              ) {
                                // update the aspect ratio setting without user confirmation, if the 'isActiveStates.preserveAspectRatio' is not mixed across the selected icons:
                                handleKeycapChange(
                                  'icons',
                                  {
                                    'isActiveStates.preserveAspectRatio':
                                      !resolveSetting({
                                        path: 'isActiveStates.preserveAspectRatio',
                                        defaultValue: false, // should be true, but we use ! in front of the resolveSetting call (false -> true)
                                        mixedReturn: false, // should be true, but we use ! in front of the resolveSetting call (false -> true)
                                      }),
                                  },
                                  selectedIconIndex
                                );
                              }
                            }}
                          >
                            {isMixed(
                              commonIconSettings,
                              'isActiveStates.preserveAspectRatio'
                            ) ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="#fe4a49"
                                className="bi bi-lock-fill"
                                viewBox="0 0 16 16"
                              >
                                <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2m3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2" />
                              </svg>
                            ) : resolveSetting({
                                path: 'isActiveStates.preserveAspectRatio',
                                defaultValue: false,
                                mixedReturn: false,
                              }) ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor"
                                className="bi bi-lock-fill"
                                viewBox="0 0 16 16"
                              >
                                <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2m3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor"
                                className="bi bi-unlock-fill"
                                viewBox="0 0 16 16"
                              >
                                <path d="M11 1a2 2 0 0 0-2 2v4a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h5V3a3 3 0 0 1 6 0v4a.5.5 0 0 1-1 0V3a2 2 0 0 0-2-2" />
                              </svg>
                            )}
                          </Button>

                          <div
                            className="icon-height-container"
                            title={mixedAspectRatioTitle}
                          >
                            <Form.Label>
                              {t('icon-editor-component-height')}
                            </Form.Label>

                            <div
                              className={`${
                                isMixed(
                                  commonIconSettings,
                                  'isActiveStates.preserveAspectRatio'
                                )
                                  ? 'disabled-icon-entry-settings'
                                  : ''
                              }`}
                            >
                              <CustomInput
                                position="center"
                                value={
                                  isControlDisabled
                                    ? defaultStyle.height
                                    : tempHeight
                                } // Use temporary height for input value (or the default, when no keycap is selected)
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  setTempHeight(inputValue); // Update the temporary height state with the input value, allowing the UI to reflect user input dynamically.
                                }}
                                onBlur={createIconDimensionBlur('height')}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault(); // Prevent the default Enter key action
                                    e.target.blur(); // Manually trigger onBlur event
                                  }
                                }}
                                unitSymbol="mm"
                                step="0.1"
                              />
                            </div>
                          </div>

                          {selectedPngDpiQuality && (
                            <div className="png-dpi-quality-container">
                              <Badge
                                className={`png-dpi-quality-badge ${selectedPngDpiQuality.className}`}
                                title={selectedPngDpiQuality.label}
                              >
                                {selectedPngDpiQuality.displayDpi} DPI
                              </Badge>
                            </div>
                          )}

                          <Button
                            variant="none"
                            className="d-none d-sm-block invert-icon-horizontally-button no-css"
                            title={t(
                              'icon-editor-component-invert-horizontally'
                            )}
                            onClick={createFlipClick('horizontal')}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M21.88 19.656a.75.75 0 0 1-.63.344h-7.5a.75.75 0 0 1-.75-.75V2.75a.75.75 0 0 1 1.433-.31l7.5 16.5a.75.75 0 0 1-.052.716M14.5 6.213V18.5h5.585zM2.5 20a.5.5 0 0 1-.452-.713l8-17A.5.5 0 0 1 11 2.5v17a.5.5 0 0 1-.5.5z"
                              ></path>
                            </svg>
                          </Button>

                          <Button
                            variant="none"
                            className="d-none d-sm-block invert-icon-vertically-button no-css"
                            title={t('icon-editor-component-invert-vertically')}
                            onClick={createFlipClick('vertical')}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M19.656 2.12a.75.75 0 0 1 .344.63v7.5a.75.75 0 0 1-.75.75H2.75a.75.75 0 0 1-.31-1.433l16.5-7.5a.75.75 0 0 1 .716.052M6.213 9.5H18.5V3.915zM20 21.5a.5.5 0 0 1-.713.452l-17-8A.5.5 0 0 1 2.5 13h17a.5.5 0 0 1 .5.5z"
                              ></path>
                            </svg>
                          </Button>
                        </div>

                        {/* The following Button is only displayed on screens <= 575. On wider screens it is placed before the icon-dimensions-container */}
                        {!pngIncompatibleControlsDisabled && (
                          <SplitButton
                            className={`d-sm-none ${
                              isControlDisabled ||
                              pngIncompatibleControlsDisabled
                                ? 'disabled-custom-split-button'
                                : 'custom-split-button'
                            } ${
                              resolveSetting({
                                path: 'isActiveStates.fill',
                                defaultValue: false,
                                mixedReturn: false,
                              })
                                ? 'custom-split-button-enabled'
                                : ''
                            }`}
                            variant="none"
                            title={
                              <svg
                                className="icon-color-svg"
                                viewBox="0 0 24 24"
                                version="1.1"
                                id="svg1"
                              >
                                <path
                                  fill="currentColor"
                                  d="M 8.65,18.5 2.5,12.35 Q 2.25,12.1 2.125,11.8 2,11.5 2,11.175 2,10.85 2.125,10.55 2.25,10.25 2.5,10 L 8.25,4.275 5.6,1.625 7.15,0 l 10,10 q 0.25,0.25 0.363,0.55 0.113,0.3 0.112,0.625 -0.001,0.325 -0.112,0.625 -0.111,0.3 -0.363,0.55 L 11,18.5 Q 10.75,18.75 10.45,18.875 10.15,19 9.825,19 9.5,19 9.2,18.875 8.9,18.75 8.65,18.5 M 9.825,5.85 4.475,11.2 h 10.7 z M 19.8,19 Q 18.9,19 18.275,18.362 17.65,17.724 17.65,16.8 q 0,-0.675 0.338,-1.275 0.338,-0.6 0.762,-1.175 L 19.8,13 l 1.1,1.35 q 0.4,0.575 0.75,1.175 0.35,0.6 0.35,1.275 0,0.925 -0.65,1.563 Q 20.7,19.001 19.8,19"
                                  id="path1"
                                />
                                <rect
                                  id="rect1"
                                  width="22.169491"
                                  height="3.4576271"
                                  x="0.91525459"
                                  y="20.542374"
                                  stroke={
                                    targets.length > 0 ||
                                    (exactlyOneSelected && selectedIconIndex)
                                      ? majorityIconColors.fill
                                      : selectedColorEntry !== null
                                        ? colors[selectedColorEntry]
                                        : '#000000'
                                  }
                                  fill={
                                    targets.length > 0 ||
                                    (exactlyOneSelected && selectedIconIndex)
                                      ? majorityIconColors.fill
                                      : selectedColorEntry !== null
                                        ? colors[selectedColorEntry]
                                        : '#000000'
                                  }
                                />
                              </svg>
                            }
                            drop="up-centered"
                            onClick={() => {
                              if (
                                isControlDisabled ||
                                pngIncompatibleControlsDisabled
                              )
                                return;

                              handleKeycapChange(
                                'icons',
                                {
                                  'isActiveStates.fill': !resolveSetting({
                                    path: 'isActiveStates.fill',
                                    defaultValue: false,
                                    mixedReturn: false,
                                  }),
                                },
                                selectedIconIndex
                              );
                            }}
                          >
                            <div className="d-flex justify-content-center mb-5">
                              <ChromePicker
                                className={`icon-editor-chrome-picker ${
                                  (isControlDisabled &&
                                    selectedColorEntry === null) ||
                                  (pngIncompatibleControlsDisabled &&
                                    selectedColorEntry === null)
                                    ? 'disabled-icon-entry-settings'
                                    : ''
                                }`}
                                color={
                                  targets.length > 0 ||
                                  (exactlyOneSelected && selectedIconIndex)
                                    ? majorityIconColors.fill
                                    : selectedColorEntry !== null
                                      ? colors[selectedColorEntry]
                                      : '#000000'
                                }
                                onChange={createIconColorChangeHandler({
                                  setting: 'fill',
                                  settingPath: 'style.fill',
                                })}
                              />
                            </div>

                            <div className="icon-color-color-picker-container">
                              <ColorList
                                colors={colors}
                                selectedIndex={selectedColorEntry}
                                handleColorButtonClick={
                                  handleIconColorButtonClick
                                }
                                handleAddColor={handleAddIconColor}
                                deleteMode={deleteIconColorMode}
                                toggleDeleteMode={toggleDeleteIconColorMode}
                                keycapSetting={'style.fill'}
                                setting={'fill'}
                                currentColor={majorityIconColors.fill}
                                hasSelection={
                                  uniformIconColorInfo.fill.hasSelection
                                }
                                isUniformSelection={
                                  uniformIconColorInfo.fill.isUniform
                                }
                                uniformSelectedIndex={
                                  uniformIconColorInfo.fill.uniformIndex
                                }
                                showUnselectButton={showUnselectButton}
                                unselectKeycaps={unselectKeycaps}
                              />
                            </div>
                          </SplitButton>
                        )}

                        {/* The following Button is only displayed on screens <= 575. On wider screens it is incorporated inside the icon-dimensions-container */}
                        <Button
                          variant="light"
                          className="d-sm-none individuel-invert-icon-horizontally-button"
                          title={t('icon-editor-component-invert-horizontally')}
                          onClick={createFlipClick('horizontal')}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path
                              fill="currentColor"
                              d="M21.88 19.656a.75.75 0 0 1-.63.344h-7.5a.75.75 0 0 1-.75-.75V2.75a.75.75 0 0 1 1.433-.31l7.5 16.5a.75.75 0 0 1-.052.716M14.5 6.213V18.5h5.585zM2.5 20a.5.5 0 0 1-.452-.713l8-17A.5.5 0 0 1 11 2.5v17a.5.5 0 0 1-.5.5z"
                            ></path>
                          </svg>
                        </Button>

                        {/* The following Button is only displayed on screens <= 575. On wider screens it is incorporated inside the icon-dimensions-container */}
                        <Button
                          variant="light"
                          className="d-sm-none individuel-invert-icon-vertically-button"
                          title={t('icon-editor-component-invert-vertically')}
                          onClick={createFlipClick('vertical')}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path
                              fill="currentColor"
                              d="M19.656 2.12a.75.75 0 0 1 .344.63v7.5a.75.75 0 0 1-.75.75H2.75a.75.75 0 0 1-.31-1.433l16.5-7.5a.75.75 0 0 1 .716.052M6.213 9.5H18.5V3.915zM20 21.5a.5.5 0 0 1-.713.452l-17-8A.5.5 0 0 1 2.5 13h17a.5.5 0 0 1 .5.5z"
                            ></path>
                          </svg>
                        </Button>

                        {!pngIncompatibleControlsDisabled && (
                          <SplitButton
                            className={`${
                              isControlDisabled ||
                              pngIncompatibleControlsDisabled
                                ? 'disabled-custom-split-button'
                                : 'custom-split-button'
                            } ${
                              resolveSetting({
                                path: 'isActiveStates.stroke',
                                defaultValue: false,
                                mixedReturn: false,
                              })
                                ? 'custom-split-button-enabled'
                                : ''
                            }`}
                            variant="none"
                            title={
                              <svg
                                className="icon-stroke-svg"
                                viewBox="0 0 16 16"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="m 5.1655988,10.748746 q 0.062295,0.451636 0.3036859,0.817616 0.2413913,0.36598 0.6229453,0.622945 0.3893408,0.256965 0.9032707,0.397128 0.5139299,0.140163 1.1290884,0.140163 0.5840112,0 1.0745806,-0.14795 0.4905695,-0.147949 0.8487633,-0.412701 0.36598,-0.264752 0.568437,-0.630732 0.202458,-0.365981 0.202458,-0.794256 0,-0.739747 -0.560651,-1.2225297 Q 9.7053128,9.0356466 8.5061431,8.7397476 L 7.0344348,8.3581936 Q 6.2479663,8.1635232 5.6795287,7.8831978 5.1188779,7.6028724 4.7528975,7.2213184 4.394704,6.8397644 4.2156072,6.3569818 q -0.17131,-0.4905695 -0.17131,-1.105728 0,-0.7319607 0.2881123,-1.3393324 Q 4.6283085,3.3045497 5.1655988,2.8762748 5.7028891,2.4402131 6.4504235,2.2066086 7.2057447,1.9652173 8.1168022,1.9652173 q 0.8331894,0 1.5495765,0.2413913 0.7163873,0.2336045 1.2458903,0.6540926 0.529504,0.4204881 0.848763,1.0044993 0.31926,0.5840112 0.365981,1.2848247 H 10.678665 Q 10.608584,4.7061767 10.390553,4.3635568 10.180309,4.01315 9.8376887,3.7717587 9.5028556,3.5225806 9.0512202,3.3979915 8.6073717,3.2656156 8.0700813,3.2656156 q -0.5762244,0 -1.0512202,0.1401627 Q 6.5516521,3.545941 6.216819,3.802906 5.8897727,4.0520841 5.7028891,4.4102777 5.5237924,4.7684712 5.5237924,5.2045329 q 0,0.3348332 0.124589,0.6073717 0.1245891,0.2647518 0.3893409,0.4905695 0.2647517,0.2180308 0.6852398,0.3971276 0.4204881,0.1790968 1.020073,0.3270463 l 1.2069565,0.3192595 q 0.8954839,0.2258176 1.5262164,0.5217167 0.630732,0.2881122 1.035646,0.677453 0.404915,0.3893408 0.591798,0.8876971 0.186884,0.4983562 0.186884,1.1368747 0,0.794256 -0.303686,1.440562 -0.295899,0.638518 -0.85655,1.090154 -0.560651,0.443848 -1.3549058,0.68524 -0.7942553,0.249178 -1.783181,0.249178 -0.9188444,0 -1.6819524,-0.233605 Q 5.5471528,13.567574 4.986502,13.139299 4.4258512,12.711024 4.0910181,12.103652 3.7639719,11.496281 3.7094641,10.748746 Z"
                                  fill="#ffffff"
                                  stroke="#000000"
                                  strokeWidth="0.5"
                                  fillRule="evenodd"
                                />
                              </svg>
                            }
                            drop="up-centered"
                            onClick={() => {
                              if (
                                isControlDisabled ||
                                pngIncompatibleControlsDisabled
                              )
                                return;

                              handleKeycapChange(
                                'icons',
                                {
                                  'isActiveStates.stroke': !resolveSetting({
                                    path: 'isActiveStates.stroke',
                                    defaultValue: false,
                                    mixedReturn: false,
                                  }),
                                },
                                selectedIconIndex
                              );
                            }}
                          >
                            <ChromePicker
                              className={`icon-editor-chrome-picker ${
                                (isControlDisabled &&
                                  selectedColorEntry === null) ||
                                (pngIncompatibleControlsDisabled &&
                                  selectedColorEntry === null)
                                  ? 'disabled-icon-entry-settings'
                                  : ''
                              }`}
                              color={
                                targets.length > 0 ||
                                (exactlyOneSelected && selectedIconIndex)
                                  ? majorityIconColors.stroke
                                  : selectedColorEntry !== null
                                    ? colors[selectedColorEntry]
                                    : '#000000'
                              }
                              onChange={createIconColorChangeHandler({
                                setting: 'stroke',
                                settingPath: 'style.stroke',
                              })}
                            />

                            <div className="stroke-color-picker-container">
                              <ColorList
                                colors={colors}
                                selectedIndex={selectedColorEntry}
                                handleColorButtonClick={
                                  handleIconColorButtonClick
                                }
                                handleAddColor={handleAddIconColor}
                                deleteMode={deleteIconColorMode}
                                toggleDeleteMode={toggleDeleteIconColorMode}
                                keycapSetting={'style.stroke'}
                                setting={'stroke'}
                                currentColor={majorityIconColors.stroke}
                                hasSelection={
                                  uniformIconColorInfo.stroke.hasSelection
                                }
                                isUniformSelection={
                                  uniformIconColorInfo.stroke.isUniform
                                }
                                uniformSelectedIndex={
                                  uniformIconColorInfo.stroke.uniformIndex
                                }
                                showUnselectButton={showUnselectButton}
                                unselectKeycaps={unselectKeycaps}
                              />
                            </div>

                            <div
                              className={`icon-stroke-thickness-container ${
                                isControlDisabled ||
                                pngIncompatibleControlsDisabled
                                  ? 'disabled-icon-entry-settings'
                                  : ''
                              }`}
                            >
                              <Form.Label>
                                {t('icon-editor-component-stroke-width')}

                                <OverlayTrigger
                                  trigger="click"
                                  rootClose // close the popover when the user clicks outside of it
                                  placement="top"
                                  overlay={
                                    <Popover>
                                      <Popover.Body>
                                        <em
                                          style={{
                                            color: '#ff4d4f',
                                            fontSize: '0.9em',
                                          }}
                                        >
                                          {t(
                                            'icon-editor-component-stroke-width-popover'
                                          )}
                                        </em>
                                      </Popover.Body>
                                    </Popover>
                                  }
                                >
                                  <Button className="info-popover">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="#707070"
                                      className="bi bi-info-circle"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                    </svg>
                                  </Button>
                                </OverlayTrigger>
                              </Form.Label>

                              <CustomInput
                                position="top"
                                value={resolveSetting({
                                  path: 'style.strokeWidth',
                                  defaultValue: 0.5,
                                  mixedReturn: '__MIXED__',
                                })}
                                onChange={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  handleKeycapChange(
                                    'icons',
                                    {
                                      'style.strokeWidth': Number(
                                        e.target.value
                                      ), // using Number() to ensure that if the user deletes the input value, atleast a zero is set
                                    },
                                    selectedIconIndex
                                  );
                                }}
                                onBlur={(e) => {
                                  const number = e.currentTarget.valueAsNumber; // get e.target.value (but as a number, if it is a number inside a string)
                                  if (!Number.isFinite(number)) return; // if number is not a number (e.g. NaN, +Infinity or -Infinity), skip commiting the value inside the handleKeycapChange function

                                  const inputValue = parseFloat(e.target.value);
                                  let newThickness =
                                    inputValue >= 0
                                      ? parseFloat(
                                          parseFloat(inputValue).toFixed(2)
                                        ) // Round to two decimal places and ensure it's a number
                                      : 0;

                                  handleKeycapChange(
                                    'icons',
                                    {
                                      'style.strokeWidth': newThickness,
                                    },
                                    selectedIconIndex
                                  );
                                }}
                                unitSymbol="mm"
                                step="0.1"
                              />
                            </div>
                          </SplitButton>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>

        <div id="icon-library" className="icon-search-container">
          <div
            className={`icon-search-heading-container ${
              isControlDisabled ? 'disabled-icon-search-container' : ''
            }`}
          >
            <h3>{t('icon-editor-component-icon-search-heading')}</h3>

            <OverlayTrigger
              trigger="click"
              rootClose // close the popover when the user clicks outside of it
              placement="bottom"
              overlay={
                <Popover>
                  <Popover.Header as="h3">
                    {t('icon-editor-component-search-icons-popover-1')}
                  </Popover.Header>

                  <Popover.Body>
                    <ul className="icon-search-popover-list">
                      <li>
                        <Trans
                          i18nKey="icon-editor-component-search-icons-popover-2"
                          components={{ strong: <strong /> }}
                        />
                      </li>
                      <li>
                        <Trans
                          i18nKey="icon-editor-component-search-icons-popover-3"
                          components={{ strong: <strong /> }}
                        />
                      </li>
                      <li>
                        <Trans
                          i18nKey="icon-editor-component-search-icons-popover-4"
                          components={{ strong: <strong /> }}
                        />
                      </li>
                    </ul>
                    <br />
                    <em className="icon-search-popover-note">
                      {t('icon-editor-component-search-icons-popover-5')}
                    </em>
                  </Popover.Body>
                </Popover>
              }
            >
              <Button className="info-popover">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="#707070"
                  className="bi bi-info-circle"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                </svg>
              </Button>
            </OverlayTrigger>

            {screenWidthXL && (
              <Button
                variant="link"
                onClick={() => setShowIconSearch(!showIconSearch)}
                aria-expanded={showIconSearch}
                className="toggle-settings-arrow"
              >
                {showIconSearch ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-caret-up-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-caret-down-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                  </svg>
                )}
              </Button>
            )}
          </div>

          {(screenWidthXL && showIconSearch) || !screenWidthXL ? (
            <div
              className={`icon-search-wrapper ${
                isControlDisabled ? 'disabled-icon-search-container' : ''
              }`}
            >
              <Nav
                justify
                variant="tabs"
                activeKey={activeIconLibraryTab}
                onSelect={handleIconLibraryTabSelect}
                className="icon-library-tabs"
              >
                <Nav.Item title={t('icon-editor-component-built-in')}>
                  <Nav.Link eventKey="built-in" disabled={isControlDisabled}>
                    {t('icon-editor-component-built-in')}
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item title={t('icon-editor-component-uploaded')}>
                  <Nav.Link eventKey="uploaded" disabled={isControlDisabled}>
                    <div className="uploaded-icon-tab-label">
                      {t('icon-editor-component-uploaded')}
                      <Badge className="new-feature-badge uploaded-icon-new-badge">
                        {t('icon-editor-component-new')}
                      </Badge>
                    </div>
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {activeIconLibraryTab === 'built-in' && (
                <>
                  <div className="icon-search-bar-container">
                    <div className="w-100">
                      <Form.Control
                        type="text"
                        className="customizer-mobile-device-compatible-center"
                        placeholder={t(
                          'icon-editor-component-search-for-icons'
                        )}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            searchIcons({
                              nextQuery: query,
                              nextCollectionPrefix: selectedCollectionPrefix,
                            });
                          }
                        }}
                      />
                    </div>

                    <DropdownButton
                      id="dropdown-basic-button"
                      variant="none"
                      className="no-css icon-filter-button"
                      drop="down-centered"
                      title={
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi funnel"
                          viewBox="0 0 16 16"
                        >
                          <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5zm1 .5v1.308l4.372 4.858A.5.5 0 0 1 7 8.5v5.306l2-.666V8.5a.5.5 0 0 1 .128-.334L13.5 3.308V2z" />
                        </svg>
                      }
                    >
                      <div className="icon-filter-dropdown-content">
                        <Form.Group>
                          <Form.Label className="icon-filter-label">
                            {t('icon-editor-component-filter-by-icon-set')}
                          </Form.Label>

                          <Form.Select
                            value={selectedCollectionPrefix}
                            onChange={(e) => {
                              const nextPrefix = e.target.value;
                              setSelectedCollectionPrefix(nextPrefix);
                            }}
                            className="customizer-mobile-device-compatible-center"
                          >
                            <option value="all">
                              {t('icon-editor-component-all-icon-sets')}
                            </option>

                            {Object.entries(availableCollections)
                              .sort((a, b) =>
                                a[1].name.localeCompare(b[1].name)
                              )
                              .map(([prefix, collection]) => (
                                <option key={prefix} value={prefix}>
                                  {collection.name} ({prefix})
                                </option>
                              ))}
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </DropdownButton>

                    <Button
                      variant="secondary"
                      onClick={() =>
                        searchIcons({
                          nextQuery: query,
                          nextCollectionPrefix: selectedCollectionPrefix,
                        })
                      }
                    >
                      {t('icon-editor-component-search')}
                    </Button>
                  </div>

                  <div
                    className={`icon-search-results-container${
                      isLoadingIcons ? ' is-loading' : ''
                    }`}
                  >
                    {isLoadingIcons && <LoadingLogo />}

                    {!isLoadingIcons &&
                      visibleIcons.map((icon) => {
                        const [collectionName] = icon.split(':');
                        const collection =
                          collections[collectionName] ||
                          availableCollections[collectionName] ||
                          {}; // Access the collection data of the clicked icon

                        return (
                          <Button
                            variant="none"
                            key={icon}
                            title={`${t(
                              'icon-editor-component-icon-info-1'
                            )}${icon}${t('icon-editor-component-icon-info-2')}${
                              collection?.name || ''
                            }${t('icon-editor-component-icon-info-3')}${
                              collection?.author?.name || ''
                            }${t('icon-editor-component-icon-info-4')}${
                              collection?.license?.title || ''
                            }${t('icon-editor-component-icon-info-5')}`}
                            onClick={async () => {
                              // 1) Fetch raw SVG string
                              let rawSVG;
                              try {
                                rawSVG = await fetchIconifySvg(icon);
                              } catch (err) {
                                toast.error(
                                  t('icon-editor-component-svg-fetch-error')
                                );
                                return;
                              }

                              // 2) Check if it’s animated
                              if (isAnimatedSVG(rawSVG)) {
                                toast(
                                  t(
                                    'icon-editor-component-animated-icons-not-supported'
                                  )
                                );
                                return;
                              }

                              // 3) Not animated => apply the icon at its SVG's size, scaled so that its larger dimension is 10 mm:
                              const updates = iconifyIconUpdates({
                                icon,
                                collection,
                                size: parseSvgDimensions(rawSVG),
                              });
                              handleKeycapChange(
                                'icons',
                                updates,
                                selectedIconIndex
                              );

                              // update the UI width and height values:
                              setTempWidth(updates['style.width']);
                              setTempHeight(updates['style.height']);
                            }}
                          >
                            <Icon
                              icon={icon}
                              width="26" // Type: number or string (Can use units like px, %, em, etc.)
                            />
                          </Button>
                        );
                      })}
                  </div>
                </>
              )}

              {activeIconLibraryTab === 'uploaded' && (
                <>
                  <div className="user-icon-upload-container">
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Group
                        controlId="user-icon-upload"
                        className="flex-grow-1 mb-0"
                      >
                        <Form.Control
                          type="file"
                          accept=".svg,.png,image/svg+xml,image/png"
                          className="customizer-mobile-device-compatible-center"
                          disabled={
                            isControlDisabled ||
                            isUploadingUserIcon ||
                            isDeletingUserIcons
                          }
                          onClick={(e) => {
                            if (!userInfo) {
                              e.preventDefault(); // stop the file picker from opening
                              toast.error(
                                t(
                                  'icon-editor-component-sign-in-to-upload-icons'
                                )
                              );
                            }
                          }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];

                            if (file) {
                              uploadUserIconHandler(file);
                            }

                            e.target.value = '';
                          }}
                        />
                      </Form.Group>

                      {userIconPreviews.length > 0 && (
                        <div className="delete-uploaded-icons-button-container">
                          <Button
                            variant="none"
                            disabled={
                              isControlDisabled ||
                              isUploadingUserIcon ||
                              userIconPreviews.length === 0
                            }
                            onClick={() => {
                              setIsDeletingUserIcons(!isDeletingUserIcons);
                            }}
                          >
                            {isDeletingUserIcons ? (
                              <div>
                                <svg fill="#fe4a49" viewBox="0 0 16 16">
                                  <g
                                    id="SVGRepo_iconCarrier"
                                    transform="matrix(0.30544377,0,0,0.30544377,-0.55018817,-0.55242556)"
                                  >
                                    <path d="m 41.1483,34.9258 3.5625,3.5625 1.1485,-24.0938 h 2.3439 c 0.9609,0 1.7342,-0.7969 1.7342,-1.7578 0,-0.9609 -0.7733,-1.7812 -1.7342,-1.7812 H 37.9608 V 7.3398 c 0,-3.3515 -2.2734,-5.5312 -5.8828,-5.5312 h -8.7656 c -3.6094,0 -5.8594,2.1797 -5.8594,5.5312 v 3.0235 h -0.8203 l 4.0078,4.0312 h 21.4688 z m 8.1795,17.25 c 0.7032,0.7031 1.8754,0.7031 2.555,0 0.6797,-0.7266 0.7032,-1.8516 0,-2.5547 L 6.6717,4.3867 c -0.7031,-0.7031 -1.8749,-0.7265 -2.578,0 -0.6797,0.6797 -0.6797,1.8516 0,2.5313 z M 21.203,10.8555 V 7.5742 c 0,-1.3359 0.9609,-2.2734 2.3907,-2.2734 h 8.2265 c 1.4297,0 2.4141,0.9375 2.4141,2.2734 v 3.2813 z m -4.2187,43.3359 h 21.4921 c 2.5079,0 4.4297,-1.2656 5.2032,-3.2812 l 0.0234,-0.4454 -3.1172,-3.0937 -0.0234,0.9141 c -0.0703,1.3828 -1.0547,2.3906 -2.4609,2.3906 H 17.3124 c -1.336,0 -2.3438,-1.0313 -2.4141,-2.3906 L 13.539,20.3242 9.6952,16.4805 11.2186,48.6836 c 0.1641,3.375 2.3672,5.5078 5.7657,5.5078 z m 12.164,-31.3125 v -2.625 c 0,-0.8437 -0.6562,-1.5234 -1.4297,-1.5234 -0.3984,0 -0.7968,0.2109 -1.0078,0.539 v 1.1954 z m 7.3125,7.3594 0.2813,-9.961 c 0.0234,-0.8906 -0.5625,-1.5468 -1.3594,-1.5468 -0.7266,0 -1.3359,0.6797 -1.3594,1.5234 l -0.2109,7.3359 z M 20.7812,46.5508 c 0.7968,0 1.3827,-0.6797 1.3593,-1.5235 l -0.4922,-16.5937 -2.789,-2.8125 0.539,19.4062 c 0.0235,0.8907 0.6094,1.5235 1.3829,1.5235 z m 6.9374,0 c 0.7735,0 1.4297,-0.6797 1.4297,-1.5235 v -9.1406 l -2.8359,-2.7891 v 11.9297 c 0,0.8438 0.6562,1.5235 1.4062,1.5235 z m 6.961,0 c 0.7734,0 1.3359,-0.6328 1.3594,-1.5235 l 0.0703,-2.1562 -2.6485,-2.6719 -0.1406,4.8281 c -0.0234,0.8438 0.5625,1.5235 1.3594,1.5235 z" />
                                  </g>
                                </svg>
                              </div>
                            ) : (
                              <svg fill="#000000" viewBox="0 0 16 16">
                                <g
                                  id="SVGRepo_iconCarrier"
                                  transform="matrix(0.30544377,0,0,0.30544377,-0.55242556,-0.55242556)"
                                >
                                  <path d="m 44.5235,48.6602 1.6172,-34.2657 h 2.3437 c 0.961,0 1.7343,-0.7969 1.7343,-1.7578 0,-0.9609 -0.7733,-1.7812 -1.7343,-1.7812 H 38.2422 V 7.3398 c 0,-3.3515 -2.2734,-5.5312 -5.8827,-5.5312 h -8.7657 c -3.6094,0 -5.8594,2.1797 -5.8594,5.5312 v 3.5157 H 7.5391 c -0.9375,0 -1.7578,0.8203 -1.7578,1.7812 0,0.9609 0.8203,1.7578 1.7578,1.7578 H 9.8829 L 11.5,48.6836 c 0.1641,3.375 2.3907,5.5078 5.7657,5.5078 h 21.4922 c 3.3516,0 5.6016,-2.1563 5.7656,-5.5312 z M 21.4844,7.5742 c 0,-1.3359 0.9844,-2.2734 2.3907,-2.2734 h 8.2265 c 1.4297,0 2.4141,0.9375 2.4141,2.2734 v 3.2813 H 21.4844 Z m -3.8671,43.1016 c -1.3594,0 -2.3673,-1.0313 -2.4376,-2.3906 L 13.5391,14.3945 h 28.8516 l -1.5469,33.8907 c -0.0703,1.3828 -1.0547,2.3906 -2.4375,2.3906 z m 17.3437,-4.125 c 0.7734,0 1.3594,-0.6328 1.3828,-1.5235 l 0.7031,-24.75 c 0.0235,-0.8906 -0.5859,-1.5468 -1.3828,-1.5468 -0.7265,0 -1.3359,0.6797 -1.3593,1.5234 l -0.7032,24.7734 c -0.0234,0.8438 0.5625,1.5235 1.3594,1.5235 z m -13.8984,0 c 0.7969,0 1.3828,-0.6797 1.3593,-1.5235 L 21.7188,20.2539 c -0.0234,-0.8437 -0.6562,-1.5234 -1.3828,-1.5234 -0.7969,0 -1.3828,0.6562 -1.3594,1.5468 l 0.7266,24.75 c 0.0234,0.8907 0.5859,1.5235 1.3594,1.5235 z m 8.3672,-1.5235 V 20.2539 c 0,-0.8437 -0.6329,-1.5234 -1.4063,-1.5234 -0.7735,0 -1.4297,0.6797 -1.4297,1.5234 v 24.7734 c 0,0.8438 0.6562,1.5235 1.4297,1.5235 0.75,0 1.4063,-0.6797 1.4063,-1.5235 z" />
                                </g>
                              </svg>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`icon-search-results-container${
                      showUserIconLibraryLoading ? ' is-loading' : ''
                    }`}
                  >
                    {showUserIconLibraryLoading && <LoadingLogo />}

                    {!showUserIconLibraryLoading &&
                      userIconPreviews?.map((userIcon) => {
                        const canDeleteThisIcon = userIcon.canDelete === true;
                        const isDeletingThisIcon =
                          deletingUserIconId === userIcon._id;

                        return (
                          <Button
                            variant="none"
                            key={userIcon._id}
                            title={
                              isDeletingUserIcons
                                ? canDeleteThisIcon
                                  ? t(
                                      'icon-editor-component-delete-uploaded-icon-title',
                                      {
                                        filename:
                                          userIcon.displayName ||
                                          userIcon.originalFilename,
                                        format: userIcon.format,
                                      }
                                    )
                                  : t(
                                      'icon-editor-component-delete-own-icons-only'
                                    )
                                : t(
                                    'icon-editor-component-uploaded-icon-title',
                                    {
                                      filename:
                                        userIcon.displayName ||
                                        userIcon.originalFilename,
                                      format: userIcon.format,
                                    }
                                  )
                            }
                            disabled={
                              isControlDisabled ||
                              isDeletingThisIcon ||
                              (isDeletingUserIcons && !canDeleteThisIcon)
                            }
                            onClick={() => {
                              if (isDeletingUserIcons) {
                                deleteUserIconHandler(userIcon); // the button is disabled for an icon the account may not delete, with the reason as its tooltip (see `disabled` and `title` above); a toast with the same reason here never ran and was deleted, the button kept `disabled` rather than the clickable looks-disabled pattern (user decision, Oct 2026)
                                return;
                              }

                              const updates = uploadedIconUpdates(userIcon);
                              handleKeycapChange(
                                'icons',
                                updates,
                                selectedIconIndex
                              );

                              // update the UI width and height values:
                              setTempWidth(updates['style.width']);
                              setTempHeight(updates['style.height']);
                            }}
                            className={`${
                              isDeletingUserIcons ? 'delete-mode' : ''
                            } user-icon-preview-button ${
                              isDeletingThisIcon
                                ? 'user-icon-preview-button-deleting'
                                : ''
                            }`}
                          >
                            {isDeletingUserIcons && canDeleteThisIcon && (
                              <span className="user-icon-delete-indicator">
                                ×
                              </span>
                            )}

                            <img
                              src={userIcon.previewUrl}
                              alt={
                                userIcon.displayName ||
                                userIcon.originalFilename
                              }
                              width="26"
                              height="26"
                              onError={() => {
                                refreshAllRelevantUserIconPreviewsOnce(); // refreshes signed preview URLs for uploaded icons if one has expired or failed to load
                              }}
                              className="user-icon-preview-image"
                            />

                            {!isDeletingUserIcons && (
                              <span className="user-icon-format-badge">
                                .{userIcon.format}
                              </span>
                            )}
                          </Button>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div></div>
          )}
        </div>
      </div>
    );
  }
);

export default IconEditor;
